import { i18n } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type {
  CampaignAssetCoverage,
  ContentFormat,
  ContentPlanSlot,
  ReportableAssetKind,
} from '@magig/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import {
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CampaignHealthPanel } from '../components/campaign-health-panel.js';
import {
  CampaignNarrativeRail,
  IsItWorking,
  type MagProposal,
  MagRecommends,
  WhyISayThat,
} from '../components/campaign-narrative.js';
import { CampaignRestrictedBanner } from '../components/campaign-restricted-banner.js';
import { CampaignRollupBlock } from '../components/campaign-rollup-block.js';
import { CampaignSearchTerms } from '../components/campaign-search-terms.js';
import { ConfirmDialog } from '../components/confirm-dialog.js';
import {
  CreativeDetailDrawer,
  GalleryTile,
  groupCreativeAssets,
} from '../components/creative-tile.js';
import { DangerZone } from '../components/danger-zone.js';
import { MagChatDrawer } from '../components/feed-home/chat/mag-chat-drawer.js';
import type { HomeChatLiveEvent } from '../components/feed-home/composers/ask-mag-composer.js';
import { FollowupDetailDrawer } from '../components/followup-detail-drawer.js';
import { InsightChatDrawer } from '../components/insight-chat-drawer.js';
import { InstagramPostPreview } from '../components/instagram-post-preview.js';
import { InvestigationFooter } from '../components/investigation-footer.js';
import { Mag } from '../components/mag.js';
import { MagMessageBody } from '../components/mag-message-body.js';
import { RsaRewriteDrawer } from '../components/rsa-rewrite-drawer.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import {
  type AnalyticsEventDeliverable,
  type AutopilotAuditEntryDTO,
  AutopilotScopeGateError,
  AutopilotTrustGateError,
  applyCampaignHealthAction,
  applyFunnelBudgetRaise,
  approveAutodraft,
  askMagChatStreaming,
  type CampaignBudgetResult,
  type CampaignDetail,
  type CampaignGeoDTO,
  type CampaignInsight,
  type CreativeAssetFormat,
  chatOnInsightStreaming,
  deleteCampaign,
  dismissFeedCard,
  type FeedCard,
  fetchCampaignAsks,
  fetchCampaignAutopilot,
  fetchCampaignAutopilotAudit,
  fetchMetaConnection,
  type GoogleSearchAdDeliverable,
  generateDeliverableImage,
  getCampaignGeo,
  type InstagramPostDeliverable,
  type MagFollowup,
  type MetaPaidAdDeliverable,
  type PaidAdHookDeliverable,
  pauseCampaign,
  pauseDeliverable,
  publishDeliverable,
  publishOrganicMetaSlot,
  refreshGoogleSearchAdStatus,
  refreshMetaPaidAdMetrics,
  renameCampaign,
  resolveFollowup,
  resumeCampaign,
  revertSlotRewrite,
  runCreativePipeline,
  scanCampaign,
  sendMagChatTurnStreaming,
  setCampaignAutopilot,
  setCampaignBudget,
  setCampaignGoal,
  setCampaignScanCadence,
  triggerGooglePaidStrategy,
  triggerMetaContentPlanRevise,
  triggerMetaPaidStrategy,
  triggerMetaPostDraft,
  triggerMetaSlotImage,
  triggerMetaSlotVideo,
  triggerPaidScheduleDraft,
  unpauseDeliverable,
} from '../lib/api.js';
import { auditRowMeta } from '../lib/audit-row.js';
import { useBrandTitle } from '../lib/document-title.js';
import { draftToPlanView } from '../lib/draft-to-plan.js';
import { env } from '../lib/env.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import {
  formatDate,
  formatDateTime,
  formatNumber,
  formatRelativeTimeCompact,
  relativeDayWord,
} from '../lib/format.js';
import {
  adAccountsQuery,
  brandsQuery,
  campaignCreativeAssetsQuery,
  campaignQuery,
  campaignRollupQuery,
  meQuery,
} from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';
import {
  campaignServingState,
  deliverableServingState,
  type ServingState,
  servingReasonPhrase,
} from '../lib/serving-state.js';
import { useAdMoney } from '../lib/use-ad-money.js';
import { DeliverablesPanel } from './brand.$slug.campaigns.new.review.$id.js';

// CAMPAIGN DETAILS (s135; s155) — canonical campaign-home page for a
// launched campaign. Hosts the goal block, ongoing reads, and scan
// history. The hero (still at /brand/$slug/campaigns/new/launched/$id)
// is a one-shot success screen; subsequent visits redirect with a
// localStorage flag and the dashboard's other entry points (campaigns
// list, home feed card) link straight here. The s126 cadence picker
// is gone — Mag picks the cadence after every scan (see s155).

/** The `t` that useLingui() hands back, so the helpers that run
 *  outside a component can take it as an argument. */
type T = ReturnType<typeof useLingui>['t'];

export const Route = createFileRoute('/brand/$slug/campaigns/$id')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const campaign = await queryClient.ensureQueryData(campaignQuery(params.id));
    if (!campaign) throw redirect({ to: '/brands' });
    // Mid-pipeline campaigns shouldn't land on the details page — bounce
    // them to the right stage. Same redirect ladder as the old launched
    // route had, minus the launched leaf.
    if (campaign.status === 'intake') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/brief',
        params: { slug: params.slug },
        search: { campaignId: campaign.id },
      });
    }
    if (campaign.status === 'drafting') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/drafting/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    // organic_meta operates per-slot from this detail page (no unified
    // "launch" review step), so 'ready' is a valid landing state — keep
    // the founder here instead of bouncing to /new/review, which itself
    // redirects organic_meta straight back here (infinite loop).
    if (campaign.status === 'ready' && campaign.kind !== 'organic_meta') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/review/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    return { me, campaign };
  },
  loader: ({ context }) => context as { me: unknown; campaign: CampaignDetail },
  component: CampaignDetailsPage,
});

/** [spec preflight-the-plan S3] WHERE THIS AD ACTUALLY RUNS.
 *
 *  Until this line existed, nothing in the product could answer it: geo was
 *  write-only, and in September a Uruguayan founder's campaign spent a month
 *  serving globally with every performance surface calling it healthy. It was
 *  found by the founder opening the Google Ads UI by chance.
 *
 *  THE EMPTY CASE IS THE LOUD ONE. No location criterion means worldwide on
 *  google, so [] gets the warning treatment and a failed read stays quiet and
 *  says so. Collapsing those two would either hide the expensive case or invent
 *  an alarm.
 *
 *  IT OWNS ITS OWN QUERY, deliberately. The obvious placement — one fetch in
 *  PlanDeliverablesSection, passed down — put a hook after that component's
 *  `if (total === 0) return` early return, which breaks hook order on a campaign
 *  with no deliverables. react-query dedupes by key, so one instance per
 *  deliverable still makes exactly one request. */
/** [spec ads/google-campaign-assets, S2b] WHAT GOOGLE WILL SHOW THAT THIS AD
 *  IS NOT USING.
 *
 *  Found because the founder opened google's own Recommendations page and
 *  asked what it all was — four of the eight were asset formats magig has
 *  never produced, and nothing in magig could say so.
 *
 *  SILENT IN TWO CASES, and the difference between them is the point.
 *  `measured: false` means the snapshot predates the asset read or covers a
 *  different google campaign on a multi-deliverable row — we did not look,
 *  and this renders exactly as the page did before the field existed. An
 *  empty `missing` means we looked and nothing is absent, which deserves no
 *  line either. Only a measured gap says anything.
 *
 *  DELIBERATELY NOT A FEED CARD. The founder reported 98 emails in a week on
 *  the same day this was found; a new card kind would have been one more.
 *  This is a line on the page they already open when they care. */
function CampaignAssetsLine({ coverage }: { coverage: CampaignAssetCoverage | undefined }) {
  const { t } = useLingui();
  if (!coverage?.measured) return null;
  if (coverage.missing.length === 0) return null;
  const label: Record<ReportableAssetKind, string> = {
    sitelink: t`sitelinks`,
    structuredSnippet: t`snippets`,
    callout: t`callouts`,
    image: t`images`,
  };
  const missing = coverage.missing.map((kind) => label[kind]).join(', ');
  return (
    <p className="text-[12px] text-[var(--ink)]/40">
      <Trans>
        google can show {missing} alongside this ad and there are none set — it takes up less of the
        result than it could.
      </Trans>
    </p>
  );
}

function CampaignGeoLine({
  campaignId,
  deliverableId,
}: {
  campaignId: string;
  deliverableId: string;
}) {
  const geoQ = useQuery({
    queryKey: ['campaign-geo', campaignId],
    queryFn: () => getCampaignGeo(campaignId),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const geo: CampaignGeoDTO | undefined = (geoQ.data ?? []).find(
    (g) => g.deliverableId === deliverableId,
  );
  if (!geo) return null;
  if (geo.countries === null) {
    return (
      <p className="text-[12px] text-[var(--ink)]/40">
        <Trans>could not read where this is running right now.</Trans>
      </p>
    );
  }
  if (geo.countries.length === 0) {
    return (
      <p className="text-[12px] font-medium text-[var(--plum)]">
        <Trans>
          running WORLDWIDE — this campaign has no country set, so it can serve anywhere. that is
          almost never what you want.
        </Trans>
      </p>
    );
  }
  return (
    <p className="text-[12px] text-[var(--ink)]/60">
      <Trans>running in {geo.countries.join(', ')}</Trans>
    </p>
  );
}

function CampaignDetailsPage() {
  const { campaign } = Route.useLoaderData();
  const { slug } = Route.useParams();
  useBrandTitle(slug, [campaign.name ?? 'campaign']);
  const shortId = campaign.id.replace(/-/g, '').slice(0, 5);
  const cmpId = `cmp_${shortId}`;
  const shipped = campaign.launchedAt ? new Date(campaign.launchedAt) : null;

  // Spec 5614cfab slice 1. These hooks sit above the organic_meta early
  // return on purpose — hooks must run in the same order on every
  // render, and organic_meta returns before the paid layout below.
  const { t: tPage } = useLingui();
  const rollupQ = useQuery(campaignRollupQuery(campaign.id));
  const [applyingBudget, setApplyingBudget] = useState(false);
  // [spec c785b77b C-10] The deliverable whose rewrite drawer is open, or null.
  // Keyed by deliverable rather than a boolean because the drawer addresses one
  // ad, and a campaign can hold more than one google_search_ad.
  const [rewriteDeliverableId, setRewriteDeliverableId] = useState<string | null>(null);
  // Invalidating the ROUTER as well as the query cache, because the two feed
  // different halves of this page: THE AD ASSETS COME FROM useLoaderData, so a
  // rollup-only refresh left the deliverables panel rendering the ad the swap
  // had just removed, until a full navigation. Every other deliverable mutation
  // on this page already invalidates the router for the same reason.
  const pageRouter = useRouter();
  // The two seeded-chat handlers lived here. They went with the page's inline
  // MagChatDrawer: setChatOpen was their only opener and the rewrite lever was
  // its only caller, so once that lever opened the diff instead, nothing could
  // reach them.
  const pageQueryClient = useQueryClient();
  const railMoney = useAdMoney();
  // The verdict reads the SHORTEST window. The rollup block below keeps
  // its own 7/14/30 tabs; this one does not get a control, because a
  // headline the founder can change is a headline they have to think
  // about, and the point of the block is that they should not have to.
  const verdictWindow =
    (rollupQ.data?.windows ?? []).find((w) => w.windowDays === 7) ??
    (rollupQ.data?.windows ?? [])[0] ??
    null;
  const budgetProposal = verdictWindow?.budgetProposal ?? null;
  const rewriteProposal = verdictWindow?.rewriteProposal ?? null;
  // [spec one-campaign-page-one-answer] THE ONE ANSWER, ranked server-side
  // over the funnel lever, campaign health's findings and the serving state
  // together. Before this, three surfaces answered "what should i do?" with
  // no knowledge of each other: this card said "nothing for you to decide"
  // while eight raise-bid buttons sat below it.
  const advice = rollupQ.data?.advice ?? null;
  // The funnel lever is the one answer that comes with a BUTTON, so it keeps
  // the proposal slot. Every other top answer is prose, and showing the
  // lever's button beside it would put back the second voice.
  const adviceIsLever = advice?.top.kind === 'funnel_lever';
  //
  // The autopilot shapes come through here too: they are prose with no button,
  // exactly like the refusals. Only 'nothing' is still excluded, because its
  // sentence is the component's own fallback — passing it would render the
  // same words twice.
  const adviceNote =
    advice && advice.top.kind !== 'funnel_lever' && advice.top.kind !== 'nothing'
      ? advice.top.line
      : null;
  // [spec 4758bfde] When mag is about to act, the founder can still stop her.
  // The timestamp is what makes the sentence a commitment rather than an
  // announcement, so it travels with the line or not at all.
  const adviceActsAt =
    advice?.top.kind === 'autopilot_next' ? (advice.top.executeAt ?? null) : null;
  // Why the bid findings lost, when they did — threaded down to the health
  // panel, which drops its buttons and keeps the numbers (D2).
  const bidsSuppressedReason =
    advice?.suppressed.find((sup) => sup.candidate.kind === 'priced_bids')?.reason ?? null;
  // [spec one-campaign-page-one-answer, RES-2] The two standing offers —
  // "other wording to try", "spend more on your best days" — are empty-state
  // affordances that render off the campaign's SHAPE, never its state, so
  // they appeared unchanged beneath a verdict that had just ruled both levers
  // out. The founder saw "spend more on your best days" directly under "the
  // budget can't reach this market": one of those is a budget instruction and
  // the other says budget is the wall.
  //
  // The spec declined to decide this and left it as residue. Seeing it on the
  // page decided it: when the verdict is that the campaign cannot work AS
  // CONFIGURED, an offer to spend mag's effort on copy or on dayparting is
  // not a softer alternative, it is the same contradiction one heading down.
  //
  // Only the two REFUSAL shapes suppress them. A funnel lever, a priced bid
  // or a clean bill of health all leave something worth exploring, and on a
  // healthy campaign these offers are the only content the block has.
  //
  // [spec 4758bfde] The EMPTY shapes join the refusals. "nothing for you to
  // decide right now" rendered directly above two things to decide — the same
  // self-contradiction, surviving in the one branch the first pass did not
  // cover, and the founder found it again six days later on another campaign.
  // Under autopilot the offers are wronger still: mag is the one acting.
  const adviceRefuses =
    advice?.top.kind === 'budget_cannot_test' ||
    advice?.top.kind === 'not_serving' ||
    advice?.top.kind === 'nothing' ||
    advice?.top.kind === 'autopilot_next' ||
    advice?.top.kind === 'autopilot_waiting';
  // AT MOST ONE OF THESE IS EVER NON-NULL — the two refuse on opposite sides
  // of the same comparison (campaign-funnel-read.ts, control C-3), and a test
  // pins that no input yields both. The ?? here is not a tiebreak, it is a
  // pick-whichever-exists; if it ever starts behaving like a tiebreak, C-3
  // has been broken upstream.
  const magProposal: MagProposal | null = rewriteProposal
    ? {
        headline: tPage`rewrite the ad`,
        line: rewriteProposal.line,
        actionLabel: tPage`show me what you'd change`,
        onAct: () => {
          // [spec c785b77b C-10] OPENS THE DIFF, not a chat about the diff.
          //
          // This seeded a mag_chat between 2026-10-01 and this change, because
          // the founder rejected RsaRewriteDrawer's idle phase (decision
          // bbafaa1). The cost was named and accepted then, and it has since
          // come due: with the drawer unmounted there was no apply path in the
          // dashboard at all, so "show me what you'd change" ended in a
          // conversation and the founder still could not change anything. The
          // drawer no longer has an idle phase — it reads the ad on open — so
          // the reason it was unmounted is gone.
          //
          // Mag chat is still one tap away on this page's bubble, so this
          // trades a conversation for an action rather than removing the
          // conversation.
          setRewriteDeliverableId(rewriteProposal.deliverableId);
        },
      }
    : budgetProposal
      ? {
          headline: tPage`raise the daily budget`,
          // Mag's own sentence, server-authored, carrying the arithmetic.
          // Not re-written here: the numbers and the words that justify
          // them must come from one place (campaign-funnel-read.ts).
          line: budgetProposal.line,
          actionLabel: tPage`raise to $${budgetProposal.newDailyBudgetUsd}/day`,
          busy: applyingBudget,
          onAct: async () => {
            setApplyingBudget(true);
            try {
              await applyFunnelBudgetRaise(
                campaign.id,
                budgetProposal.deliverableId,
                budgetProposal.newDailyBudgetUsd,
              );
              // Re-read rather than patching local state from the
              // response: the platform is the truth and it lags.
              await pageQueryClient.invalidateQueries({
                queryKey: campaignRollupQuery(campaign.id).queryKey,
              });
              magToast.success(
                tPage`budget raised to $${budgetProposal.newDailyBudgetUsd}/day.`,
                tPage`the next read will show what it opened up.`,
              );
            } catch (err) {
              magToast.error(founderErrorCopy(err));
            } finally {
              setApplyingBudget(false);
            }
          },
        }
      : null;

  // [spec 3a365634] The asks mag has filed about this campaign. Same
  // predicate as the NEEDS YOU count on the list, so the row and the
  // page cannot disagree. 30s stale: these change when a scheduler
  // writes a card, not on the founder's keystrokes.
  //
  // ABOVE the organic_meta early return below, and that placement is
  // load-bearing rather than tidy: a hook after a conditional return
  // runs in a different order depending on campaign kind, so navigating
  // from an organic campaign to a paid one would desync React's hook
  // list and crash. Biome caught this; I had anchored it next to
  // dailyBudget, which lives after the return.
  const { data: asksData } = useQuery({
    queryKey: ['campaign', campaign.id, 'asks'],
    queryFn: () => fetchCampaignAsks(campaign.id),
    staleTime: 30_000,
  });
  const campaignAsks = (asksData ?? []).map((c) => ({
    id: c.id,
    title: c.title,
    body: c.body,
  }));
  // [task a14c174] Clear an ask from the page it is about. The drawer could
  // always do this; reaching it took a navigation, a read of a restatement of
  // the page behind it, and a footer click — for a card the founder had
  // already dealt with, while the nav's needs-you dot stayed lit.
  //
  // DISMISS, NOT ACK. "i've got this" is the founder declining the card, not
  // confirming they did what it asked. Same invalidation set the drawer uses,
  // so the dot drops with the row.
  const [dismissingAskId, setDismissingAskId] = useState<string | null>(null);
  const onDismissAsk = async (askId: string) => {
    setDismissingAskId(askId);
    try {
      await dismissFeedCard(askId);
      await pageQueryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'asks'] });
      await pageQueryClient.invalidateQueries({ queryKey: ['brand-decision-count'] });
      await pageQueryClient.invalidateQueries({ queryKey: ['home-decisions'] });
      await pageQueryClient.invalidateQueries({ queryKey: ['feed'] });
    } catch (err) {
      magToast.error(founderErrorCopy(err));
    } finally {
      setDismissingAskId(null);
    }
  };

  // s233.10 — organic_meta gets a settings-style two-column shell:
  // left sidebar carries identity/status/autopilot/stats (so the
  // founder reads "what kind of push is this" at a glance); right
  // column is the plan content. Paid + analytics kinds keep the
  // single-column layout — refactoring those is a separate pass
  // (their sections carry heavier per-row tooling that would need
  // restyling individually to thread the same hierarchy).
  if (campaign.kind === 'organic_meta') {
    return (
      <main className="min-h-screen bg-[var(--cream)]">
        <TopNav active="campaigns" />
        <section className="mx-auto max-w-[1240px] px-4 pb-16 pt-7 sm:px-7">
          <Link
            to="/brand/$slug/campaigns"
            params={{ slug }}
            className="link-quiet inline-block text-[12px]"
          >
            {'←'} <Trans>back to campaigns</Trans>
          </Link>
          <div className="mt-5 grid grid-cols-1 gap-7 lg:grid-cols-[300px_1fr] lg:gap-10">
            <OrganicMetaSidebar campaign={campaign} cmpId={cmpId} shipped={shipped} />
            <article className="min-w-0">
              <OrganicMetaPlanSection
                campaign={campaign}
                brandSlug={slug}
                brandId={campaign.brandId}
              />
            </article>
          </div>
        </section>
      </main>
    );
  }

  const { kindLabel, pill, serving } = campaignIdentity(campaign, tPage);
  const dailyBudget = dailyBudgetUsd(campaign);

  // The one action worth offering at the top of this page, and only while
  // there is one. Founder-reported 2026-10-09: the launch control sat below
  // the keyword lists inside the ad block and was missed entirely, on a page
  // whose own header already said NOT LIVE YET.
  //
  // Exactly ONE pending google ad, deliberately. `unlaunchedPaidAdCount`
  // counts never-launched AND mock-launched deliverables (a mock is not on
  // the platform), which is the right set — but with several of them there is
  // no honest single button: launching "all" is a behaviour this page has
  // never had, and picking one silently would launch the wrong ad. The
  // per-ad buttons below remain the way to handle that case.
  const pendingGoogleAds =
    campaign.status !== 'archived' && campaign.draft
      ? draftToPlanView(campaign.draft).deliverables.filter(
          (d): d is GoogleSearchAdDeliverable =>
            d.kind === 'google_search_ad' &&
            (!('launched_at' in d && d.launched_at) ||
              ('launched_via' in d && d.launched_via === 'mock')),
        )
      : [];
  const solePendingGoogleAd = pendingGoogleAds.length === 1 ? pendingGoogleAds[0] : undefined;

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active="campaigns" />
      <section className="mx-auto max-w-[1240px] px-4 pb-16 pt-7 sm:px-7">
        <Link
          to="/brand/$slug/campaigns"
          params={{ slug }}
          className="link-quiet inline-block text-[12px]"
        >
          {'←'} <Trans>back to campaigns</Trans>
        </Link>
        {/* Spec 5614cfab slice 1 — the same shell organic_meta uses 30
            lines up. Two campaign kinds disagreeing about where identity
            lives is worse than either arrangement is good. */}
        <div className="mt-5 grid grid-cols-1 gap-7 lg:grid-cols-[300px_1fr] lg:gap-10">
          <CampaignNarrativeRail
            campaign={campaign}
            cmpId={cmpId}
            shippedLabel={shipped ? `shipped ${formatRelative(shipped)}` : null}
            chips={<IdentityChips kindLabel={kindLabel} pill={pill} />}
            autopilot={<AutopilotToggle campaign={campaign} dailyBudget={dailyBudget} />}
            {...(solePendingGoogleAd
              ? {
                  primaryAction: (
                    <LaunchGoogleAdAction
                      campaign={campaign}
                      deliverableId={solePendingGoogleAd.id}
                    />
                  ),
                }
              : {})}
            lifecycle={<CampaignPauseBlock campaign={campaign} />}
            stats={
              /* NOT servingNote. The restricted banner at the top of the
                 main column already says "there's something off with the
                 budget" in Mag's voice; repeating it here under an "at a
                 glance" heading made the page say one thing twice and
                 left the rail with no actual facts on it. */
              dailyBudget !== null || verdictWindow ? (
                <>
                  {dailyBudget !== null ? (
                    <BudgetStat campaign={campaign} dailyBudget={dailyBudget} />
                  ) : null}
                  {verdictWindow ? (
                    <SidebarStat
                      label={tPage`spent`}
                      value={railMoney(verdictWindow.totalSpendUsd)}
                    />
                  ) : null}
                  {verdictWindow ? (
                    <SidebarStat
                      label={tPage`clicks`}
                      value={formatNumber(verdictWindow.totalClicks, { maximumFractionDigits: 0 })}
                    />
                  ) : null}
                </>
              ) : null
            }
            changes={
              campaign.shortPitch ? (
                <div className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
                    {'↘'} <Trans>why i built it this way</Trans>
                  </p>
                  <p className="mt-2 mag-voice text-[14px] leading-snug text-[var(--ink)]/70">
                    “{campaign.shortPitch}”
                  </p>
                </div>
              ) : null
            }
          />

          <article className="campaign-narrative-main flex min-w-0 flex-col gap-6">
            <MissingConnectionsBanner campaign={campaign} slug={slug} />

            {/* A ceiling the founder can lift belongs ABOVE the fold, not in a
                tooltip on the ad tile. Renders nothing unless google is actually
                restricting the campaign or the funnel read can price a lever, so
                a healthy page is unchanged. */}
            <CampaignRestrictedBanner campaign={campaign} />

            {/* s195 — InsightsPanel renders on any campaign mag can scan:
                analytics_setup (events) AND any campaign with launched
                meta_paid_ad deliverables (paid metrics). */}
            {campaign.kind === 'analytics_setup' ? (
              <InsightsPanel campaign={campaign} />
            ) : !campaign.draft ? (
              /* EVERY paid surface below this line reads campaign.draft —
                 hasMetaPaidDeliverables, hasGooglePaidDeliverables,
                 campaignPlatforms, launchedPaidDeliverables and the whole
                 deliverables panel. With no draft they all render nothing, and
                 the page used to fall through to OtherKindTombstone, whose copy
                 blames the campaign KIND ("proactive insights live only on
                 analytics_setup campaigns for now") for what is actually a
                 missing plan. On a campaign that has shipped and is spending,
                 that is a misdiagnosis shown to the founder on the one screen
                 they opened to find out what is happening. */
              <MissingPlanPanel
                campaign={campaign}
                shippedLabel={shipped ? `shipped ${formatRelative(shipped)}` : null}
                window={verdictWindow}
              />
            ) : (
              <>
                {campaign.kind === 'creative_ads' ? (
                  <>
                    {/* ① is it working — one glossed sentence, three
                        glossed numbers, no trade vocabulary (C-1). */}
                    <IsItWorking window={verdictWindow} serving={serving} />

                    {/* ② mag recommends — exactly one proposal.
                        budgetProposal is the only priced lever today; it
                        refuses itself when the loss is ad rank, which is
                        the hole slice 2 fills with rewriteProposal.
                        pendingDecisionNote now carries the resolver's
                        answer — the slot this comment reserved. It is
                        read from one ranking over the funnel lever,
                        campaign health and the serving state, so this
                        card can no longer say "nothing to decide"
                        while the page below it offers eighteen
                        controls that decide something. */}
                    <MagRecommends
                      proposal={adviceIsLever || !advice ? magProposal : null}
                      pendingDecisionNote={adviceNote}
                      actsAt={adviceActsAt}
                      asks={campaignAsks}
                      onDismissAsk={onDismissAsk}
                      dismissingAskId={dismissingAskId}
                    >
                      {adviceRefuses ? null : (
                        <>
                          {hasMetaPaidDeliverables(campaign) ? (
                            <>
                              <PaidStrategyPanel campaign={campaign} />
                              <PaidSchedulePanel campaign={campaign} channel="meta_paid" />
                            </>
                          ) : null}
                          {hasGooglePaidDeliverables(campaign) ? (
                            <>
                              <GooglePaidStrategyPanel campaign={campaign} />
                              <PaidSchedulePanel campaign={campaign} channel="google_paid" />
                            </>
                          ) : null}
                        </>
                      )}
                    </MagRecommends>

                    {/* ③ why i say that — closed by default. Every tile,
                        table, roster and footnote the old page showed at
                        peer weight with the verdict lives in here, and
                        the trade vocabulary is legal again. */}
                    <WhyISayThat>
                      <CampaignRollupBlock
                        campaignId={campaign.id}
                        platforms={campaignPlatforms(campaign)}
                      />
                      <PlanDeliverablesSection
                        campaign={campaign}
                        slot="health"
                        bidsSuppressedReason={bidsSuppressedReason}
                      />
                    </WhyISayThat>

                    {/* ④ the ad itself. */}
                    <PlanDeliverablesSection campaign={campaign} slot="deliverables" />
                  </>
                ) : (
                  <PlanDeliverablesSection campaign={campaign} />
                )}
                {hasScannablePaidAds(campaign) ? <InsightsPanel campaign={campaign} /> : null}
                {/* Creative drafting is for meta/instagram deliverables only —
                    the formats are all meta-native, so a google-only campaign
                    doesn't need this section. */}
                {needsCreativeSection(campaign) ? (
                  <CampaignCreativeSection campaign={campaign} />
                ) : null}
              </>
            )}

            {/* The action log is a changelog. It was the SECOND thing on
                this page; it is now the last, because nothing about it
                helps a founder decide anything. */}
            <AutopilotAuditLog campaign={campaign} />

            {/* Last thing on the page, as in settings. A destructive action
                should be something you scroll to, not something you pass. */}
            <CampaignDangerZone campaign={campaign} slug={slug} />

            {/* [spec c785b77b C-10] THE REWRITE DIFF, mounted again.
                This comment used to explain why it was not: the founder had
                asked for a seeded mag chat instead (decision bbafaa1), which
                left the dashboard with no apply path at all — and so left
                control C-7 ("the apply cannot be reached without the component
                that renders the diff") VACATED rather than satisfied, true only
                because nothing could apply. An autonomous rewrite exists now,
                so the founder-facing half is load-bearing again: C-7 is
                re-derived here rather than inherited from that green state.

                Mounted only while open: the Drawer shell owns the enter
                AND exit animation via useDrawer. */}
            {rewriteDeliverableId ? (
              <RsaRewriteDrawer
                // KEYED BY DELIVERABLE, so switching ads remounts instead of
                // reusing a drawer whose once-on-mount draft already fired. A
                // second ad would otherwise render the first one's diff and
                // offer to apply it, defeating the per-ad addressing this state
                // exists to provide.
                key={rewriteDeliverableId}
                campaignId={campaign.id}
                brandId={campaign.brandId}
                deliverableId={rewriteDeliverableId}
                onClose={() => setRewriteDeliverableId(null)}
                onApplied={async () => {
                  // Re-read rather than patching local state from the 200: the
                  // platform is the truth and it lags. Same reason the budget
                  // lever beside it invalidates instead of mutating.
                  await pageQueryClient.invalidateQueries({
                    queryKey: campaignRollupQuery(campaign.id).queryKey,
                  });
                  // And the loader, which is what the ad assets render from.
                  await pageRouter.invalidate();
                }}
              />
            ) : null}

            {/* The page's inline MagChatDrawer stood here. It is gone with the
                seeded-chat path that was its only opener — chatOpen could never
                become true again. Mag chat is still one tap away on the global
                launcher bubble, which is what the C-10 note above means by
                trading a conversation for an action rather than removing the
                conversation. */}
          </article>
        </div>
      </section>
    </main>
  );
}

/** Banner that surfaces above the autopilot card when the campaign needs
 *  a paid-platform connection the brand hasn't wired up yet. Each missing
 *  platform gets its own row with a "connect google ads" / "connect meta"
 *  button that links to the api's OAuth start endpoint. The OAuth flow
 *  redirects back to a callback page; on completion the connection is
 *  live and the next launch attempt hits the real client.
 *
 *  Hidden when every needed platform is connected (status 'connected'),
 *  or when the campaign has no paid deliverables at all. */
function MissingConnectionsBanner({ campaign, slug }: { campaign: CampaignDetail; slug: string }) {
  const { t } = useLingui();
  const needed = campaignPlatforms(campaign);
  const accountsQ = useQuery(adAccountsQuery(campaign.brandId));
  if (needed.length === 0) return null;
  if (accountsQ.isLoading) return null;
  const connectedSet = new Set(
    (accountsQ.data?.accounts ?? []).filter((a) => a.status === 'connected').map((a) => a.provider),
  );
  const missing = needed.filter((p) => !connectedSet.has(p));
  if (missing.length === 0) return null;

  const label = (p: 'meta' | 'google'): string => (p === 'meta' ? t`meta ads` : t`google ads`);
  // Push the founder back to THIS campaign page after the OAuth round-
  // trip — the api's connect/start endpoint validates ?returnTo= against
  // an allowlist (same-origin paths only) so the redirect is safe.
  // Without it the default callback drops them at /channels.
  const returnTo = `/brand/${slug}/campaigns/${campaign.id}`;
  const connectUrl = (p: 'meta' | 'google'): string =>
    `${env.API_URL}/api/brands/${campaign.brandId}/${p}/connect/start?returnTo=${encodeURIComponent(returnTo)}`;

  const missingLabels = missing.map(label).join(' + ');
  return (
    <div className="mt-5 rounded-2xl border-[1.5px] border-[var(--pulse)]/55 bg-[var(--pulse)]/5 p-5">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--pulse)]">
        {'↘'} <Trans>no ad account connected</Trans>
      </p>
      <p className="mt-1.5 mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/85">
        <Trans>
          “this campaign needs a {missingLabels} connection before ads can ship for real. launches
          will run in mock mode until then — i'll create the ads on our side but nothing reaches the
          platform.”
        </Trans>
      </p>
      <ul className="mt-4 flex flex-col gap-2">
        {missing.map((p) => {
          const platformName = p === 'meta' ? 'meta' : 'google';
          const platformLabel = label(p);
          return (
            <li
              key={p}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--line)] bg-white px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/70">
                  {label(p)}
                </p>
                <p className="mt-0.5 text-[12.5px] leading-snug text-[var(--ink)]/65">
                  <Trans>
                    takes ~60 seconds. you'll bounce to {platformName} to authorize, then land back
                    here.
                  </Trans>
                </p>
              </div>
              <a href={connectUrl(p)} className="btn btn-pulse-solid shrink-0">
                <Trans>connect {platformLabel}</Trans>
                <span className="btn-arrow">→</span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Bar at the top of PlanDeliverablesSection that launches every
 *  not-yet-launched paid ad on the campaign.
 *
 *  It used to hide below 2 launchable ads, reasoning that "one is just
 *  a normal per-ad click". That holds only if the founder can SEE the
 *  per-ad button — and they can't: it sits at the bottom of a
 *  full-height ad card, well below the fold, under a header that
 *  already says LIVE. A founder who had approved the plan reported not
 *  knowing a launch step remained until they happened to scroll. With
 *  exactly one ad, this bar is the only thing above the fold that says
 *  so, which is precisely when it should not be hidden. */
function LaunchAllBar({
  count,
  busy,
  onLaunchAll,
}: {
  count: number;
  busy: boolean;
  onLaunchAll: () => void | Promise<void>;
}) {
  const { t } = useLingui();
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-[1.5px] border-[var(--ink)] bg-[var(--spell)] px-4 py-3 shadow-[4px_4px_0_var(--ink)]">
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/70">
          {'↘'} <Trans>ready to ship</Trans>
        </p>
        <p className="mt-1 text-[14px] leading-snug text-[var(--ink)]">
          {plural(count, {
            one: 'your ad is approved and waiting — it goes live on google only when you launch it.',
            other:
              '# paid ads approved and waiting. launch them all in one click, or open each card to launch individually.',
          })}
        </p>
      </div>
      <button
        type="button"
        onClick={onLaunchAll}
        disabled={busy}
        className="btn btn-pulse-solid disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? t`launching…` : plural(count, { one: 'launch it', other: 'launch all #' })}
        <span className="btn-arrow">→</span>
      </button>
    </div>
  );
}

/** True when the campaign has at least one deliverable that produces
 *  meta/instagram creative (a paid IG ad or an organic post). Used to
 *  gate the CampaignCreativeSection so a google-only campaign doesn't
 *  show meta-format buttons. google_search_ad creative is text-only,
 *  generated inside the drafter — there's nothing for the creative
 *  drafting surface to design. */
function needsCreativeSection(campaign: CampaignDetail): boolean {
  const draft = campaign.draft as { deliverables?: Array<{ kind?: string }> } | null;
  const deliverables = draft?.deliverables ?? [];
  return deliverables.some(
    (d) => d.kind === 'meta_paid_ad' || d.kind === 'paid_ad_hook' || d.kind === 'instagram_post',
  );
}

/** Which ad platforms the campaign's deliverables actually use. Threaded
 *  into the rollup block so a google-only campaign doesn't render an
 *  empty Meta tile (and vice versa). */
function campaignPlatforms(campaign: CampaignDetail): Array<'meta' | 'google'> {
  const out: Array<'meta' | 'google'> = [];
  if (hasMetaPaidDeliverables(campaign)) out.push('meta');
  if (hasGooglePaidDeliverables(campaign)) out.push('google');
  return out;
}

function hasMetaPaidDeliverables(campaign: CampaignDetail): boolean {
  const draft = campaign.draft as { deliverables?: Array<{ kind?: string }> } | null;
  return (draft?.deliverables ?? []).some(
    (d) => d.kind === 'meta_paid_ad' || d.kind === 'paid_ad_hook',
  );
}

function hasGooglePaidDeliverables(campaign: CampaignDetail): boolean {
  const draft = campaign.draft as { deliverables?: Array<{ kind?: string }> } | null;
  return (draft?.deliverables ?? []).some((d) => d.kind === 'google_search_ad');
}

/** s195 — true when the campaign has at least one launched
 *  meta_paid_ad deliverable. Used to decide whether to render the
 *  InsightsPanel (mag's reads) on a non-analytics campaign. Paid
 *  ads launching is the trigger; until then there's nothing for
 *  mag to read on a creative_ads campaign. */
function hasScannablePaidAds(campaign: CampaignDetail): boolean {
  const draft = campaign.draft;
  if (!draft || !('shape' in draft) || draft.shape !== 'plan') return false;
  return draft.deliverables.some(
    (d) => d.kind === 'meta_paid_ad' && Boolean(d.launched_at) && Boolean(d.launched_campaign_id),
  );
}

/** Per-campaign autopilot toggle (autopilot-roadmap.md A0). Off by
 *  default; founder explicit opt-in per campaign. Hidden on campaign
 *  kinds with no autopilot lanes (conversion_path, analytics_setup) —
 *  the api answers `channels: []` for those and this component
 *  renders nothing.
 *
 *  Phase A0 ships ONLY the toggle persistence. Subsequent phases
 *  (A1+) wire actual behavior through the new authority lookup. */
// Exported so the imported-campaign takeover view (and any other surface
// that holds a campaign id but not a full CampaignDetail) can render
// the same toggle. Internally only uses campaign.id — the rest of
// CampaignDetail was historical clutter from when the toggle lived
// solely on this page.
export function AutopilotToggle({
  campaign,
  dailyBudget = null,
}: {
  campaign: { id: string };
  /** [spec ea8ed74b] Shown in the override dialog. Optional because the
   *  imported-campaign page mounts this with an id and nothing else; the
   *  dialog drops the budget sentence rather than inventing a number. */
  dailyBudget?: number | null;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  const statusQ = useQuery({
    queryKey: ['campaign', campaign.id, 'autopilot'],
    queryFn: () => fetchCampaignAutopilot(campaign.id),
    staleTime: 30_000,
  });
  const [busy, setBusy] = useState(false);
  // [12.2L-95] Collapsed by default — once a founder has set autopilot,
  // the card just takes vertical real estate that isn't load-bearing.
  // Header (eyebrow + on/off pill + chevron) stays clickable to
  // expand when they want to flip or read mag's voice line.
  const [expanded, setExpanded] = useState(false);
  // [spec ea8ed74b] The trust-gate override confirmation. Separate from
  // `busy` so the dialog can stay open with its button spinning.
  const [overrideOpen, setOverrideOpen] = useState(false);
  // s236-3 — when the api refuses with missing_scopes, surface an
  // inline upgrade prompt with a deep-link to /connect/extend
  // instead of just a generic error toast. Cleared the moment the
  // toggle attempt is retried.
  const [scopeGate, setScopeGate] = useState<{
    missingScopes: string[];
    extendUrl: string;
  } | null>(null);

  if (statusQ.isLoading || statusQ.error || !statusQ.data) return null;
  const status = statusQ.data;
  if (status.channels.length === 0) return null;

  // [12.2L-169] Trust gate. The toggle is disabled when mag hasn't
  // earned the founder's trust yet on the publish lane of any of the
  // campaign's channels. allEligible comes from the api; when false,
  // we render the eligibility reason (mag voice) inline and refuse
  // to flip on. status.on === true bypasses the gate so the founder
  // can always turn autopilot OFF, regardless of trust state — they
  // never lose the manual override.
  // [spec ea8ed74b] ...unless the founder already waived it on this
  // campaign. The waiver sticks, so someone who vouched for mag
  // yesterday does not meet the same padlock today.
  const trustGateActive =
    status.allEligible === false && !status.on && status.trustOverridden !== true;
  // Pick the eligibility reason to display — for multi-channel
  // campaigns we surface the channel with the lowest streak (the
  // highest bar to clear). Falls back to the first channel's
  // eligibility when streaks tie.
  const trustReason: string | null = trustGateActive
    ? (() => {
        const entries = Object.entries(status.eligibility ?? {});
        const blocked = entries.filter(([, e]) => !e.eligible);
        if (blocked.length === 0) return null;
        const worst = blocked.reduce((a, b) =>
          a[1].approvedStreak <= b[1].approvedStreak ? a : b,
        );
        return worst[1].reason;
      })()
    : null;

  const onToggle = async (overrideTrustGate = false) => {
    const next = !status.on;
    setBusy(true);
    setScopeGate(null);
    try {
      await setCampaignAutopilot(campaign.id, next, overrideTrustGate);
      magToast.success(
        next ? t`autopilot on.` : t`autopilot off.`,
        next
          ? overrideTrustGate
            ? t`you vouched for me early — i'll act autonomously on this campaign. flip me back any time.`
            : t`i'll act autonomously on this campaign. you can flip me back any time.`
          : t`back to manual — nothing autonomous on this campaign.`,
      );
      setOverrideOpen(false);
      // refreshQueries takes a rest-spread of QueryKeys; the outer
      // array bracket I had here was a bug — it made the function
      // refetch a single queryKey shaped [['campaign', id,
      // 'autopilot'], ['campaign', id]] which matches nothing in
      // the cache, so the UI showed stale on/off state until a
      // manual reload.
      await refreshQueries(
        queryClient,
        ['campaign', campaign.id, 'autopilot'],
        ['campaign', campaign.id],
      );
      await router.invalidate();
    } catch (err) {
      if (err instanceof AutopilotScopeGateError) {
        setScopeGate({
          missingScopes: err.missingScopes,
          extendUrl: err.extendUrl,
        });
      } else if (err instanceof AutopilotTrustGateError) {
        // [12.2L-169] Server refused because trust streak hasn't
        // hit threshold. The toggle should already be disabled in
        // this branch (UI computes trustGateActive from the same
        // signal), but if a race let the click through (stale
        // status cache, etc.), surface the mag-voice reason as a
        // toast so the founder isn't left wondering.
        magToast.error(t`not yet.`, err.message);
      } else {
        magToast.error("couldn't toggle autopilot.", founderErrorCopy(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const channelLabel = (ch: string): string => {
    if (ch === 'meta_organic') return t`instagram organic`;
    if (ch === 'meta_paid') return t`meta ads`;
    if (ch === 'google_paid') return t`google ads`;
    return ch;
  };
  const channelText = status.channels.map(channelLabel).join(' + ');

  // Brand-aligned card — hard-edge shadow (plum off / spell on),
  // ink border, `.btn` for the primary action, italic-serif mag voice
  // for the body. Header + status pill use the breadcrumb pattern
  // (↘ eyebrow + on/off chip) so this surface reads as part of the
  // mag family alongside the brand drafts review + intake interview.
  //
  // Layout note: the briefed-campaign sidebar is ~300px wide, so this
  // header has to survive a narrow column. An earlier pass let it wrap
  // — flex-wrap + justify-between dropped the status chip onto its own
  // line — which read as two stacked rows in a card that is collapsed
  // precisely to be one. The eyebrow is now just `↘ autopilot` (the
  // channel moved into the expanded body, where it is reference detail
  // rather than state), which leaves room for chip + chevron on the
  // same line at 300px. flex-wrap stays as the safety net for a longer
  // translation of "autopilot"; the chip keeps its right edge per the
  // brand action-button rule (action buttons always right-aligned).
  return (
    <section
      className={`rounded-2xl border-[1.5px] border-[var(--ink)] bg-white transition-[padding] duration-150 ease-out ${
        expanded ? 'p-5 sm:p-6' : 'p-3.5 sm:p-4'
      } ${status.on ? 'shadow-[4px_4px_0_var(--spell)]' : 'shadow-[4px_4px_0_var(--plum)]'}`}
    >
      {/* Header — always visible. Whole row toggles expansion so the
          tap target is generous. Status pill stays as a read-out either
          way; chevron rotates to signal collapsed vs open. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full flex-wrap items-center justify-between gap-2 text-left"
      >
        <p className="shrink-0 font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>autopilot</Trans>
        </p>
        <div className="flex items-center gap-2">
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 font-mono text-[9.5px] font-bold uppercase tracking-[0.08em] ${
              status.on
                ? 'bg-[var(--spell)] text-[var(--ink)]'
                : trustGateActive
                  ? 'border border-[var(--ink)]/25 bg-[var(--paper)] text-[var(--ink)]/65'
                  : 'border border-[var(--ink)]/15 bg-[var(--paper)] text-[var(--ink)]/55'
            }`}
          >
            {status.on ? '●' : trustGateActive ? '◌' : '○'}{' '}
            {status.on ? t`on` : trustGateActive ? t`locked` : t`off`}
          </span>
          <AutopilotChevron open={expanded} />
        </div>
      </button>
      {expanded ? (
        <div className="mag-stagger-in">
          <p className="mt-3 mag-voice text-[15.5px] leading-[1.45] text-[var(--ink)]/85">
            “
            {status.on
              ? t`i'll act without waiting — publish at scheduled hours, ship brain edits when signal supports them. you stay in the loop on creative + plan revisions.`
              : trustReason
                ? trustReason
                : t`i propose; you approve. flip on when you want me to act without asking.`}
            ”
          </p>
          {/* The channel used to ride in the eyebrow, which made the
              collapsed header too wide for the 300px rail and wrapped
              the status chip onto its own line. It is reference detail,
              not state — it belongs with the voice line, not the edge.

              [2026-10-03] ...but not when the eligibility chips are
              below it. Those name every channel AND carry its streak,
              so this line rendered "google ads" directly above a chip
              reading "google ads 0/3" — the founder reported it as a
              duplicate, which it is. The chips are strictly more
              informative, so this line yields to them. */}
          {trustGateActive && status.eligibility ? null : (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/45">
              {channelText}
            </p>
          )}
          {trustGateActive && status.eligibility ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(status.eligibility).map(([channel, elig]) => {
                // Was a second, hand-inlined copy of channelLabel's
                // mapping — untranslated, and free to drift from the
                // one above it. One source for the name.
                const channelLbl = channelLabel(channel);
                return (
                  <span
                    key={channel}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ink)]/15 bg-[var(--paper)] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/70"
                    title={elig.reason}
                  >
                    <span className="text-[var(--ink)]/50">{channelLbl}</span>
                    <span
                      className={`tabular-nums ${elig.eligible ? 'text-[var(--ink)]' : 'text-[var(--plum)]'}`}
                    >
                      {elig.approvedStreak}/{elig.threshold}
                    </span>
                  </span>
                );
              })}
            </div>
          ) : null}
          {scopeGate ? (
            <div className="mt-4 rounded-xl border-[1.5px] border-[var(--pulse)]/50 bg-[var(--pulse)]/5 p-3.5 sm:p-4">
              {/* bound before the message so the placeholder is named */}
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--pulse)]">
                {'↘'} <Trans>your meta connection needs more access</Trans>
              </p>
              <p className="mt-1.5 mag-voice text-[13px] leading-[1.5] text-[var(--ink)]/80">
                <Trans>
                  “i can't run autopilot here yet — missing {scopeGate.missingScopes.join(', ')}.
                  extend your meta connection and you'll be set.”
                </Trans>
              </p>
              <div className="mt-3 flex justify-end">
                <a href={scopeGate.extendUrl} className="btn btn-sm">
                  <Trans>extend connection</Trans>
                  <span className="btn-arrow">→</span>
                </a>
              </div>
            </div>
          ) : null}
          <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
            {/* [spec ea8ed74b] The locked button stays exactly as it was —
                it is still the honest default, and mag still says she has
                not earned this. Beside it, a quieter way to say "i know,
                go anyway". Ghost rather than primary on purpose: the
                earned path should look like the main one. */}
            {trustGateActive ? (
              <button
                type="button"
                onClick={() => setOverrideOpen(true)}
                disabled={busy}
                className="btn btn-ghost btn-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trans>turn it on anyway</Trans>
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onToggle()}
              disabled={busy || trustGateActive}
              title={
                trustGateActive
                  ? t`i need to earn this. approve a few more of my drafts and the switch unlocks.`
                  : undefined
              }
              className={`btn ${status.on ? 'btn-ghost' : 'btn-spell'} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {busy
                ? t`flipping…`
                : status.on
                  ? t`switch to manual`
                  : trustGateActive
                    ? t`locked — earning trust`
                    : t`turn on autopilot`}
              {!trustGateActive || status.on ? <span className="btn-arrow">→</span> : null}
            </button>
          </div>
        </div>
      ) : null}
      {/* Names the three things the founder is actually agreeing to:
          mag acts without a click, at this budget, and it is reversible.
          Not `destructive` — this is a grant of trust, not a deletion —
          but it is a real decision and the copy says so rather than
          asking "are you sure?". */}
      <ConfirmDialog
        open={overrideOpen}
        title={t`turn on autopilot early?`}
        confirmLabel={t`yes, run on your own`}
        cancelLabel={t`not yet`}
        busy={busy}
        busyLabel={t`turning on…`}
        onCancel={() => setOverrideOpen(false)}
        onConfirm={() => onToggle(true)}
        body={
          // [2026-10-04] Was two stacked quotes, ~70 words of italic serif.
          // The first was trustReason — verbatim what the autopilot card
          // BEHIND this dialog already says, so the modal opened by
          // re-reading the founder their own screen. Same duplication they
          // caught above the eligibility chip, and they called this one
          // "too much and too hard to read".
          //
          // One sentence for what actually changes, one mono line for the
          // two reassurances. The 0/3 streak goes too: the chip behind the
          // dialog already carries it.
          <span className="flex flex-col gap-2.5">
            <span className="mag-voice block text-[15px] leading-[1.45]">
              {dailyBudget !== null ? (
                <Trans>
                  “i'll publish and spend up to ${dailyBudget}/day on {channelText} without asking
                  you first.”
                </Trans>
              ) : (
                <Trans>“i'll publish and spend on {channelText} without asking you first.”</Trans>
              )}
            </span>
            <span className="font-mono text-[10.5px] uppercase leading-[1.5] tracking-[0.1em] text-[var(--ink)]/55">
              {'↘'}{' '}
              <Trans>reversible any time · everything i do lands in this campaign's log</Trans>
            </span>
          </span>
        }
      />
    </section>
  );
}

function AutopilotChevron({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`text-[var(--ink)]/45 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/** Autopilot A8 — per-campaign "what mag did" feed. Hidden when
 *  the campaign has no audit entries (cold autopilot state). Shows
 *  the last 5 by default, expandable to all 50. Each row is a
 *  one-liner: action verb + key context (slot/deliverable) + when. */
function AutopilotAuditLog({ campaign }: { campaign: CampaignDetail }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  // The feed-card drawer is hoisted to the brand layout and sniffs
  // ?card=<id> off the live URL (brand.$slug.tsx), so opening the
  // deciding card is a search-param change on THIS page — no bounce
  // through /home, and the founder keeps their scroll position.
  const openDecision = (cardId: string) => {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, card: cardId }),
      replace: false,
    });
  };
  const auditQ = useQuery({
    queryKey: ['campaign', campaign.id, 'autopilot-audit'],
    queryFn: () => fetchCampaignAutopilotAudit(campaign.id),
    staleTime: 60_000,
  });
  const [expanded, setExpanded] = useState(false);
  if (auditQ.isLoading || auditQ.error || !auditQ.data || auditQ.data.length === 0) return null;
  const all = auditQ.data;
  const shown = expanded ? all : all.slice(0, 5);

  return (
    /* No mt-3: the campaign page's main column is a gap-6 flex column and
       owns the rhythm. The panel's own margin stacked on top of it, which is
       the third instance of this bug on this page — the ask-mag panels and
       DeliverablesPanel had it too. A shared rule is wanted here more than a
       third one-off fix, but the other two are already corrected and this is
       the last caller. */
    <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/65">
          {/* Not "mag's recent actions" any more: the log carries the
              founder's own actions now, and filing those under mag's
              name is worse than the log we had. */}
          {'↘'} <Trans>what happened here · {all.length}</Trans>
        </p>
        {all.length > 5 ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
          >
            {expanded ? t`show less` : plural(all.length, { other: 'show all #' })}
          </button>
        ) : null}
      </div>
      <ul className="mt-3 space-y-1.5">
        {shown.map((entry) => {
          const meta = auditRowMeta(entry);
          return (
            <li
              key={entry.id}
              className="flex items-baseline justify-between gap-3 border-t border-[var(--line)]/60 pt-1.5 first:border-0 first:pt-0 font-mono text-[11px]"
            >
              <span className="min-w-0 flex-1 truncate text-[var(--ink)]/80">
                {/* A refusal must not read as work done. succeeded=false
                  rows have always been written and have always rendered
                  identically to successes — so a google rejection looked
                  like a completed action. */}
                {meta.failed ? (
                  <span className="mr-1.5 text-[var(--ink)]/50">
                    <Trans>couldn't</Trans>
                  </span>
                ) : null}
                {formatAuditEntry(entry, t)}
              </span>
              <span className="shrink-0 tabular-nums text-[var(--ink)]/45">
                {/* WHO, then when — every row, never inferred from the
                  panel's title. */}
                <span
                  className={meta.who === 'mag' ? 'text-[var(--ink)]/40' : 'text-[var(--plum)]'}
                >
                  {meta.who}
                  {' · '}
                </span>
                {relativeAgo(entry.createdAt)}
                {/* Links only where a decision exists. A null source is
                  meaningful — the action ran under standing authority —
                  so it renders as no link rather than a dead one. */}
                {meta.linksToDecision ? (
                  <button
                    type="button"
                    onClick={() => openDecision(entry.sourceFeedCardId as string)}
                    className="ml-1.5 text-[var(--plum)] hover:underline"
                    title={t`see the decision this came from`}
                  >
                    ↗
                  </button>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Format one audit entry as a single-line action description.
 *  Keeps the dashboard log readable without the founder having to
 *  understand internal action_kind enums. */
function formatAuditEntry(entry: AutopilotAuditEntryDTO, t: T): string {
  const p = entry.payload as Record<string, unknown>;
  const slotId = typeof p['slotId'] === 'string' ? (p['slotId'] as string) : null;
  const deliverableId =
    typeof p['deliverableId'] === 'string' ? (p['deliverableId'] as string) : null;
  const reason = typeof p['reason'] === 'string' ? (p['reason'] as string) : null;
  const via = typeof p['via'] === 'string' ? (p['via'] as string) : null;
  const target = slotId
    ? i18n._(msg`slot ${slotId}`)
    : deliverableId
      ? i18n._(msg`ad ${deliverableId}`)
      : '';
  // The trailing clause is part of the sentence, not glued on after it:
  // a language that puts the reason before the verb cannot fix a
  // concatenation it does not own. Each branch is one whole message.
  switch (entry.actionKind) {
    case 'publish':
      return via ? i18n._(msg`published ${target} via ${via}`) : i18n._(msg`published ${target}`);
    case 'skip':
      return reason ? i18n._(msg`skipped ${target} — ${reason}`) : i18n._(msg`skipped ${target}`);
    case 'reschedule':
      return reason
        ? i18n._(msg`rescheduled ${target} — ${reason}`)
        : i18n._(msg`rescheduled ${target}`);
    case 'rewrite':
      return reason ? i18n._(msg`rewrote ${target} — ${reason}`) : i18n._(msg`rewrote ${target}`);
    case 'paid_launch':
      // In mock mode the ad row gets created locally without any
      // platform call. Audit text reflects that distinction so the
      // founder doesn't think they shipped live.
      if (via === 'mock') return i18n._(msg`created ${target} — pending real launch`);
      return via ? i18n._(msg`launched ${target} on ${via}`) : i18n._(msg`launched ${target}`);
    case 'paid_pause':
      return reason ? i18n._(msg`paused ${target} — ${reason}`) : i18n._(msg`paused ${target}`);
    case 'paid_unpause':
      return i18n._(msg`resumed ${target}`);
    case 'raise_bid': {
      const bid = typeof p['newBidUsd'] === 'number' ? (p['newBidUsd'] as number) : null;
      return bid === null
        ? i18n._(msg`raised the max cpc on ${target}`)
        : i18n._(msg`raised the max cpc on ${target} to $${bid.toFixed(2)}`);
    }
    case 'pause_keyword': {
      const kw = typeof p['keyword'] === 'string' ? (p['keyword'] as string) : null;
      return kw
        ? i18n._(msg`paused the keyword "${kw}"`)
        : i18n._(msg`paused a keyword on ${target}`);
    }
    default:
      return `${entry.actionKind} ${target}`.trim();
  }
}

function relativeAgo(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  // Empty string for unparseable or future timestamps is load-bearing —
  // callers render it inline and expect nothing rather than a fallback.
  if (Number.isNaN(ms) || ms < 0) return '';
  return formatRelativeTimeCompact(Date.parse(iso));
}

/** s216-p3.5 — organic_meta campaign view. The campaign's draft jsonb
 *  carries the MetaContentPlanOutput shape (cadence + pillars +
 *  slots). Renders the same way the standalone meta_content_plan
 *  feed card renders — but anchored on the campaign instead of the
 *  global feed. Phase 4 will add a per-slot draft surface here so
 *  the founder can review the actual caption + visual per slot. */
function OrganicMetaPlanSection({
  campaign,
  brandSlug,
  brandId,
}: {
  campaign: CampaignDetail;
  brandSlug: string;
  brandId: string;
}) {
  const { t } = useLingui();
  // s233.20 — pull the brand's name + logoUrl from the brands cache
  // (already loaded by the route via brandsQuery) so the IG preview
  // can render real identity instead of the slug placeholder.
  const brandsQ = useQuery(brandsQuery());
  const brand = brandsQ.data?.find((b) => b.id === brandId) ?? null;
  // s233.21 — when the brand has an active Meta connection, prefer
  // connectedPageName as the handle (the founder explicitly picked
  // this during the connect flow; closer to a real IG identity than
  // brand.name which can be free-form display text). Falls back to
  // brand.name otherwise. The actual IG @ username isn't stored on
  // the connection row (would need a Graph API call); page name is
  // the best stand-in until that lookup ships.
  const metaConnQ = useQuery({
    queryKey: ['brand-meta-connection', brandId],
    queryFn: () => fetchMetaConnection(brandId),
    staleTime: 5 * 60 * 1000,
  });
  const connectedHandle =
    metaConnQ.data?.status === 'active' && metaConnQ.data?.connectedPageName
      ? metaConnQ.data.connectedPageName
      : null;
  const igHandle = (connectedHandle ?? brand?.name ?? brandSlug).toLowerCase().replace(/\s+/g, '');
  // s270 — favicon-first (matches slot drawers): favicons are already
  // shaped for the round avatar crop; logoUrl is often a wide wordmark
  // and can point at a stale asset that 404s.
  const igAvatarUrl = brand?.faviconUrl ?? brand?.logoUrl ?? null;
  const queryClient = useQueryClient();
  const router = useRouter();
  const [draftingSlotId, setDraftingSlotId] = useState<string | null>(null);
  const [renderingSlotId, setRenderingSlotId] = useState<string | null>(null);
  const [reviseInstruction, setReviseInstruction] = useState('');
  const [isRevising, setIsRevising] = useState(false);
  const [publishingSlotId, setPublishingSlotId] = useState<string | null>(null);
  const [revertingSlotId, setRevertingSlotId] = useState<string | null>(null);

  const draft = campaign.draft;
  if (!draft || !('cadence' in draft) || !('slots' in draft)) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-[var(--ink)]/25 bg-[var(--paper)] p-5">
        <p className="mag-voice text-[14px] leading-[1.45] text-[var(--ink)]/65">
          <Trans>“content plan still drafting — give me a moment.”</Trans>
        </p>
      </div>
    );
  }
  const plan = draft as Extract<typeof draft, { cadence: unknown; slots: unknown[] }>;
  const formatLabels: Record<string, string> = {
    static_image: 'static',
    carousel: 'carousel',
    reel: 'reel',
    video: 'video',
  };

  const slotsByDate = new Map<string, typeof plan.slots>();
  for (const s of plan.slots) {
    const existing = slotsByDate.get(s.date) ?? [];
    existing.push(s);
    slotsByDate.set(s.date, existing);
  }
  const orderedDates = Array.from(slotsByDate.keys()).sort();

  const onDraftSlot = async (slotId: string, hasExistingDraft: boolean) => {
    setDraftingSlotId(slotId);
    try {
      await triggerMetaPostDraft(campaign.id, slotId);
      magToast.success(
        hasExistingDraft ? t`re-drafted.` : t`drafted.`,
        t`caption + visual brief ready.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't draft.", founderErrorCopy(err));
    } finally {
      setDraftingSlotId(null);
    }
  };

  const onRenderSlotImage = async (slotId: string, hadExisting: boolean) => {
    setRenderingSlotId(slotId);
    try {
      await triggerMetaSlotImage(campaign.id, slotId);
      magToast.success(
        hadExisting ? t`re-rendered.` : t`image rendered.`,
        t`attached to the slot.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't render image.", founderErrorCopy(err));
    } finally {
      setRenderingSlotId(null);
    }
  };

  // s233.5 — sibling of onRenderSlotImage for reel/video slot
  // formats. Video renders take 20-30s (vs ~5s for images), so the
  // founder sees a longer pending state. Same campaign refresh path
  // after so the slot picks up videoUrl + duration.
  const onRenderSlotVideo = async (slotId: string, hadExisting: boolean) => {
    setRenderingSlotId(slotId);
    try {
      const result = await triggerMetaSlotVideo(campaign.id, slotId);
      const seconds = result.videoDurationSeconds;
      magToast.success(
        hadExisting ? t`re-rendered.` : t`video rendered.`,
        t`${seconds}s · attached to the slot.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't render video.", founderErrorCopy(err));
    } finally {
      setRenderingSlotId(null);
    }
  };

  const onRevertRewrite = async (slotId: string) => {
    setRevertingSlotId(slotId);
    try {
      await revertSlotRewrite(campaign.id, slotId);
      magToast.success(
        t`reverted.`,
        t`restored your prior draft. autopilot will not rewrite again until a new digest lands.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't revert.", founderErrorCopy(err));
    } finally {
      setRevertingSlotId(null);
    }
  };

  const onPublishSlot = async (slotId: string) => {
    setPublishingSlotId(slotId);
    try {
      const result = await publishOrganicMetaSlot(campaign.id, slotId);
      magToast.success(
        result.via === 'meta' ? t`shipped to instagram.` : t`mock-published.`,
        result.via === 'meta'
          ? t`it's live on instagram now.`
          : t`marked shipped — connect a meta business account to publish for real next time.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't publish.", founderErrorCopy(err));
    } finally {
      setPublishingSlotId(null);
    }
  };

  const onReviseSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = reviseInstruction.trim();
    if (!trimmed || isRevising) return;
    setIsRevising(true);
    try {
      const result = await triggerMetaContentPlanRevise(campaign.id, trimmed);
      const preservedNote =
        result.draftsPreserved > 0
          ? plural(result.draftsPreserved, { one: ' · kept # draft', other: ' · kept # drafts' })
          : '';
      const before = result.slotsBefore;
      const after = result.slotsAfter;
      magToast.success(t`plan revised.`, t`${before} → ${after} slots${preservedNote}.`);
      setReviseInstruction('');
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't revise.", founderErrorCopy(err));
    } finally {
      setIsRevising(false);
    }
  };

  // Pull revision history off the plan (typed via the schema cast
  // through `Extract<…>` above). Optional + may be missing on plans
  // generated before phase 3b.
  const revisionHistory =
    (
      plan as {
        revisionHistory?: Array<{
          at: string;
          instruction: string;
          summary: string;
          costUsd: number;
        }>;
      }
    ).revisionHistory ?? [];
  const lastRevision =
    revisionHistory.length > 0 ? revisionHistory[revisionHistory.length - 1] : null;
  // Phase A1: publishMode is no longer read here — the top-level
  // AutopilotToggle owns the founder-facing state, and the scheduler
  // reads campaign authority instead. Legacy plans with publishMode
  // still set get migrated by the scheduler on first tick.

  return (
    <section className="space-y-5">
      {/* s233.10 — intro reads as Mag narrating the plan she shipped.
          Bigger serif so it carries more weight than the metadata
          cards below; "↘ mag · the plan" eyebrow grounds it as her
          voice rather than another data block. */}
      <div>
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>mag · the plan</Trans>
        </p>
        <p className="mt-2 font-serif text-[17px] leading-[1.45] text-[var(--ink)]">
          “{plan.summary}”
        </p>
      </div>

      <div className="rounded-xl border-[1.5px] border-[var(--ink)]/15 bg-white p-4 shadow-[2px_2px_0_var(--spell)]">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'}{' '}
          <Trans>
            cadence · {plan.cadence.planDays}d · {plan.cadence.postsPerWeek}/wk
          </Trans>
        </p>
        <p className="mt-2 font-serif text-[14px] leading-[1.5] text-[var(--ink)]/80">
          {plan.cadenceRationale}
        </p>
      </div>

      <form
        onSubmit={onReviseSubmit}
        className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4"
      >
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>tweak this plan</Trans>
          </p>
          {lastRevision ? (
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
              {plural(revisionHistory.length, { one: '# revision', other: '# revisions' })}
            </span>
          ) : null}
        </div>
        <p className="mt-1 mag-voice text-[13px] leading-[1.5] text-[var(--ink)]/60">
          <Trans>tell me what to change. drafts stay attached to slots that survive.</Trans>
        </p>
        <textarea
          value={reviseInstruction}
          onChange={(e) => setReviseInstruction(e.target.value)}
          placeholder={t`e.g. push more product launches next week. skip friday — i'm traveling.`}
          rows={3}
          maxLength={1000}
          disabled={isRevising}
          className="mt-3 w-full resize-y rounded-md border border-[var(--line)] bg-[var(--cream)]/40 px-3 py-2 font-serif text-[13px] leading-[1.5] text-[var(--ink)] placeholder:text-[var(--ink)]/40 focus:border-[var(--ink)]/40 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            {reviseInstruction.length}/1000
          </span>
          <button
            type="submit"
            disabled={
              isRevising ||
              reviseInstruction.trim().length === 0 ||
              draftingSlotId !== null ||
              renderingSlotId !== null
            }
            className="self-end rounded-md border border-[var(--plum)]/40 bg-[var(--plum)]/10 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)] transition hover:bg-[var(--plum)]/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRevising ? t`revising…` : t`tweak this plan`}
          </button>
        </div>

        {lastRevision ? (
          <div className="mt-3 border-t border-[var(--line)]/60 pt-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
              {'↘'} <Trans>last revision</Trans>
            </p>
            <p className="mt-1 font-mono text-[11px] text-[var(--ink)]/55">
              <Trans>you said: “{lastRevision.instruction}”</Trans>
            </p>
            <p className="mt-1 font-serif text-[13px] leading-[1.5] text-[var(--ink)]/80">
              {lastRevision.summary}
            </p>
          </div>
        ) : null}
      </form>

      {plan.pillars.length > 0 ? (
        <div className="rounded-xl border-[1.5px] border-[var(--ink)]/15 bg-white p-5 shadow-[2px_2px_0_var(--plum)]">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>pillars · {plan.pillars.length}</Trans>
          </p>
          <ul className="mt-4 space-y-3.5">
            {plan.pillars.map((p) => (
              <li
                key={p.label}
                className="border-t border-[var(--line)] pt-3.5 first:border-0 first:pt-0"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-display text-[15px] font-bold leading-snug tracking-[-0.01em] text-[var(--ink)]">
                    {p.label}
                  </p>
                  <span className="shrink-0 font-mono text-[11px] tabular-nums text-[var(--ink)]/55">
                    {p.weightPercent}%
                  </span>
                </div>
                <p className="mt-1 font-serif text-[14.5px] leading-[1.5] text-[var(--ink)]/80">
                  {p.description}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {plan.slots.length > 0 ? (
        <PlanCalendarOverview
          startDate={plan.cadence.startDate}
          planDays={plan.cadence.planDays}
          slotsByDate={slotsByDate}
        />
      ) : null}

      {plan.slots.length > 0 ? (
        <div className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>schedule · {plan.slots.length} slots</Trans>
          </p>
          <ul className="mt-4 space-y-4">
            {orderedDates.map((date) => {
              const daySlots = slotsByDate.get(date) ?? [];
              return (
                <li
                  key={date}
                  id={`slot-day-${date}`}
                  className="scroll-mt-20 border-t border-[var(--line)] pt-4 first:border-0 first:pt-0"
                >
                  <p className="font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--ink)]">
                    {date}
                  </p>
                  <ul className="mt-1.5 space-y-2">
                    {daySlots.map((s) => {
                      const slotDraft = s.draft ?? null;
                      const isDrafting = draftingSlotId === s.slotId;
                      return (
                        <li key={s.slotId} className="rounded-lg bg-[var(--cream)]/70 px-4 py-3">
                          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/65">
                            {String(s.hour).padStart(2, '0')}:00 ·{' '}
                            {formatLabels[s.format] ?? s.format} · {s.pillar}
                          </p>
                          {s.rescheduledAt && s.rescheduleReason ? (
                            <p
                              className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--plum)]"
                              title={s.rescheduleReason}
                            >
                              {'↘'} <Trans>rescheduled · {s.rescheduleReason}</Trans>
                            </p>
                          ) : null}
                          <p className="mt-1.5 font-serif text-[16px] leading-[1.4] text-[var(--ink)]">
                            {s.concept}
                          </p>
                          <p className="mt-1 font-serif text-[13.5px] leading-[1.5] text-[var(--ink)]/70">
                            {s.rationale}
                          </p>

                          {slotDraft ? (
                            <div className="mt-3 space-y-3 border-t border-[var(--line)]/60 pt-3">
                              {/* s233.19 — IG-style post preview when media is
                                  rendered; render CTA when not. Caption +
                                  media shipped together in the preview;
                                  separate caption/brief blocks were noisy. */}
                              {(() => {
                                const isVideoSlot = s.format === 'reel' || s.format === 'video';
                                const hasMedia = isVideoSlot
                                  ? !!slotDraft.videoUrl
                                  : !!slotDraft.imageUrl;
                                if (hasMedia) {
                                  return (
                                    <InstagramPostPreview
                                      mediaUrl={
                                        (isVideoSlot
                                          ? slotDraft.videoUrl
                                          : slotDraft.imageUrl) as string
                                      }
                                      mediaKind={isVideoSlot ? 'video' : 'image'}
                                      format={s.format}
                                      caption={slotDraft.caption}
                                      handle={igHandle}
                                      avatarUrl={igAvatarUrl}
                                      alt={t`preview of ${s.concept.slice(0, 60)}`}
                                    />
                                  );
                                }
                                // Pre-render state — show the caption + brief
                                // + a render CTA so the founder can review
                                // what mag wrote before producing media.
                                return (
                                  <div className="space-y-3">
                                    <div>
                                      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
                                        {'↘'} <Trans>caption</Trans>
                                      </p>
                                      <p className="mt-1.5 whitespace-pre-line font-serif text-[14.5px] leading-[1.55] text-[var(--ink)]">
                                        {slotDraft.caption}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
                                        {'↘'} <Trans>visual brief</Trans>
                                      </p>
                                      <p className="mt-1.5 font-serif text-[13px] leading-[1.55] text-[var(--ink)]/75">
                                        {slotDraft.imageBrief}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Render / re-render row — sits OUTSIDE the
                                  IG preview so the preview is purely the
                                  post itself, not a card with buttons. */}
                              {(() => {
                                const isVideoSlot = s.format === 'reel' || s.format === 'video';
                                if (isVideoSlot) {
                                  return slotDraft.videoUrl ? (
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      {slotDraft.videoDurationSeconds ? (
                                        <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
                                          {'↘'} <Trans>{slotDraft.videoDurationSeconds}s</Trans>
                                        </p>
                                      ) : (
                                        <span />
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => onRenderSlotVideo(s.slotId, true)}
                                        disabled={
                                          renderingSlotId !== null || draftingSlotId !== null
                                        }
                                        className="rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        {renderingSlotId === s.slotId
                                          ? t`re-rendering…`
                                          : t`re-render video`}
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex justify-end">
                                      <button
                                        type="button"
                                        onClick={() => onRenderSlotVideo(s.slotId, false)}
                                        disabled={
                                          renderingSlotId !== null || draftingSlotId !== null
                                        }
                                        className="rounded-md border border-[var(--plum)]/40 bg-[var(--plum)]/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)] transition hover:bg-[var(--plum)]/20 disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        {renderingSlotId === s.slotId
                                          ? t`rendering · ~20-30s…`
                                          : t`render video`}
                                      </button>
                                    </div>
                                  );
                                }
                                return slotDraft.imageUrl ? (
                                  <div className="flex justify-end">
                                    <button
                                      type="button"
                                      onClick={() => onRenderSlotImage(s.slotId, true)}
                                      disabled={renderingSlotId !== null || draftingSlotId !== null}
                                      className="rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      {renderingSlotId === s.slotId
                                        ? t`re-rendering…`
                                        : t`re-render image`}
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex justify-end">
                                    <button
                                      type="button"
                                      onClick={() => onRenderSlotImage(s.slotId, false)}
                                      disabled={renderingSlotId !== null || draftingSlotId !== null}
                                      className="rounded-md border border-[var(--plum)]/40 bg-[var(--plum)]/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)] transition hover:bg-[var(--plum)]/20 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      {renderingSlotId === s.slotId
                                        ? t`rendering…`
                                        : t`render image`}
                                    </button>
                                  </div>
                                );
                              })()}

                              {/* Visual brief — demoted to a small details
                                  toggle. The IG preview already shows what
                                  mag drew; the brief is debug-style context
                                  for "why this image?" */}
                              {slotDraft.imageUrl || slotDraft.videoUrl ? (
                                <details className="group">
                                  <summary className="cursor-pointer list-none font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/45 hover:text-[var(--ink)]/80">
                                    {'↘'} <Trans>visual brief mag used</Trans>
                                  </summary>
                                  <p className="mt-2 font-serif text-[12.5px] leading-[1.5] text-[var(--ink)]/70">
                                    {slotDraft.imageBrief}
                                  </p>
                                </details>
                              ) : null}

                              {slotDraft.rewrittenAt && slotDraft.rewriteReason ? (
                                <div className="rounded-md border border-[var(--plum)]/30 bg-[var(--plum)]/5 px-3 py-2">
                                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
                                    {'↘'} <Trans>rewritten · mag's call</Trans>
                                  </p>
                                  <p className="mt-1 font-serif text-[12px] leading-[1.5] text-[var(--ink)]/75">
                                    “{slotDraft.rewriteReason}”
                                  </p>
                                  {slotDraft.previousDraft && !slotDraft.publishedAt ? (
                                    <div className="mt-2 flex justify-end">
                                      <button
                                        type="button"
                                        onClick={() => onRevertRewrite(s.slotId)}
                                        disabled={
                                          revertingSlotId !== null ||
                                          publishingSlotId !== null ||
                                          draftingSlotId !== null ||
                                          renderingSlotId !== null
                                        }
                                        className="self-end rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        {revertingSlotId === s.slotId
                                          ? t`reverting…`
                                          : t`revert to previous draft`}
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}

                              {slotDraft.publishedAt && slotDraft.publishedPostId ? (
                                <div className="rounded-md border border-[var(--ink)]/15 bg-[var(--cream)]/40 px-3 py-2">
                                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/75">
                                    {'↘'} <Trans>published</Trans>
                                    {slotDraft.publishedVia === 'meta' ? (
                                      <Trans> · live on instagram</Trans>
                                    ) : (
                                      <Trans> · mock</Trans>
                                    )}
                                  </p>
                                  <p className="mt-1 font-mono text-[10px] tabular-nums text-[var(--ink)]/55">
                                    <Trans>post id · {slotDraft.publishedPostId}</Trans>
                                  </p>
                                </div>
                              ) : slotDraft.skippedAt && slotDraft.skipReason ? (
                                <div className="rounded-md border border-[var(--ink)]/15 bg-[var(--cream)]/40 px-3 py-2">
                                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/75">
                                    {'↘'} <Trans>skipped · mag's call</Trans>
                                  </p>
                                  <p className="mt-1 font-serif text-[12px] leading-[1.5] text-[var(--ink)]/75">
                                    “{slotDraft.skipReason}”
                                  </p>
                                  {slotDraft.imageUrl ? (
                                    <div className="mt-2 flex justify-end">
                                      <button
                                        type="button"
                                        onClick={() => onPublishSlot(s.slotId)}
                                        disabled={
                                          publishingSlotId !== null ||
                                          draftingSlotId !== null ||
                                          renderingSlotId !== null
                                        }
                                        className="self-end rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        {publishingSlotId === s.slotId
                                          ? t`publishing…`
                                          : t`publish anyway`}
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              ) : slotDraft.imageUrl ? (
                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => onPublishSlot(s.slotId)}
                                    disabled={
                                      publishingSlotId !== null ||
                                      draftingSlotId !== null ||
                                      renderingSlotId !== null
                                    }
                                    className="self-end rounded-md border border-[var(--plum)]/50 bg-[var(--plum)] px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--paper)] transition hover:bg-[var(--plum)]/90 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {publishingSlotId === s.slotId
                                      ? t`publishing…`
                                      : t`publish now`}
                                  </button>
                                </div>
                              ) : null}

                              <div className="flex items-center justify-between gap-3">
                                <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
                                  <Trans>drafted</Trans>
                                </p>
                                <button
                                  type="button"
                                  onClick={() => onDraftSlot(s.slotId, true)}
                                  disabled={
                                    isDrafting ||
                                    draftingSlotId !== null ||
                                    renderingSlotId !== null ||
                                    publishingSlotId !== null ||
                                    Boolean(slotDraft.publishedAt)
                                  }
                                  className="self-end rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {isDrafting ? t`re-drafting…` : t`re-draft`}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 flex justify-end">
                              <button
                                type="button"
                                onClick={() => onDraftSlot(s.slotId, false)}
                                disabled={
                                  isDrafting || draftingSlotId !== null || renderingSlotId !== null
                                }
                                className="self-end rounded-md border border-[var(--plum)]/40 bg-[var(--plum)]/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)] transition hover:bg-[var(--plum)]/20 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {isDrafting ? t`drafting…` : t`draft this post`}
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
        {'↘'} <Trans>confidence {plan.confidence}/10 · publishing comes in phase 5</Trans>
      </p>
    </section>
  );
}

/** s216-p4c — calendar overview for the organic-meta content plan.
 *  Renders a 7-col week grid spanning the plan's start → end window,
 *  with format-coded chips per slot per day. Click a day → scroll
 *  the schedule list below to that day's slot block (the list has
 *  matching `id="slot-day-YYYY-MM-DD"` anchors).
 *
 *  Why a separate component: the parent already has a lot of state
 *  for drafting/rendering; the calendar is pure-derive from
 *  (startDate, planDays, slotsByDate). Easier to read + test in
 *  isolation. */
function PlanCalendarOverview({
  startDate,
  planDays,
  slotsByDate,
}: {
  startDate: string;
  planDays: number;
  slotsByDate: Map<string, readonly ContentPlanSlot[]>;
}) {
  // Parse the YYYY-MM-DD start string into a UTC-midnight Date so
  // day arithmetic doesn't drift across DST. We're only working with
  // calendar dates here, never actual wall times.
  const parseYmd = (s: string): Date => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  };
  const fmtYmd = (d: Date): string => {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const start = parseYmd(startDate);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + Math.max(planDays - 1, 0));

  // Find the Monday of the week containing `start` so the grid
  // aligns to a normal week. getUTCDay returns 0=Sun..6=Sat — we
  // want Mon=0 so subtract appropriately.
  const dayOffsetMonStart = (d: Date) => (d.getUTCDay() + 6) % 7;
  const gridStart = new Date(start);
  gridStart.setUTCDate(start.getUTCDate() - dayOffsetMonStart(start));
  const gridEnd = new Date(end);
  gridEnd.setUTCDate(end.getUTCDate() + (6 - dayOffsetMonStart(end)));

  // Build the array of weeks (each = Date[7]).
  const weeks: Date[][] = [];
  for (let cur = new Date(gridStart); cur <= gridEnd; ) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cur));
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    weeks.push(week);
  }

  // Today's date in the same UTC-midnight bucketing so we can ring
  // the right cell. We use the user's local clock to derive the
  // ISO; if the brand-tz is different from the user's local tz, the
  // "today" highlight is a hair imprecise but it's just a visual
  // affordance, not an action.
  const now = new Date();
  const todayIso = fmtYmd(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));

  const onDayClick = (iso: string) => {
    const el = document.getElementById(`slot-day-${iso}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Format → chip color. Picks from the same palette the rest of
  // the brand uses so the calendar reads as part of the page, not
  // a separate widget.
  const chipColorFor = (format: ContentFormat): string => {
    if (format === 'reel') return 'bg-[var(--pulse)]';
    if (format === 'carousel') return 'bg-[var(--plum)]';
    if (format === 'video') return 'bg-[var(--ink)]/60';
    return 'bg-[var(--ink)]';
  };
  const chipShortFor = (format: ContentFormat): string => {
    if (format === 'reel') return 'R';
    if (format === 'carousel') return 'C';
    if (format === 'video') return 'V';
    return 'S';
  };

  // Pair label + key so the two "T"s (tue/thu) and two "S"s (sat/sun)
  // get stable unique keys without falling back to array index.
  const dayHeaders: Array<{ key: string; label: string }> = [
    { key: 'mon', label: 'M' },
    { key: 'tue', label: 'T' },
    { key: 'wed', label: 'W' },
    { key: 'thu', label: 'T' },
    { key: 'fri', label: 'F' },
    { key: 'sat', label: 'S' },
    { key: 'sun', label: 'S' },
  ];

  return (
    <div className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'}{' '}
          <Trans>
            calendar · {startDate} → {fmtYmd(end)}
          </Trans>
        </p>
        <div className="flex shrink-0 items-center gap-3 font-mono text-[9px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <span className="flex items-center gap-1">
            <span className="inline-block size-2 rounded-full bg-[var(--ink)]" />{' '}
            <Trans>static</Trans>
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block size-2 rounded-full bg-[var(--plum)]" />{' '}
            <Trans>carousel</Trans>
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block size-2 rounded-full bg-[var(--pulse)]" />{' '}
            <Trans>reel</Trans>
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block size-2 rounded-full bg-[var(--ink)]/60" />{' '}
            <Trans>video</Trans>
          </span>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-7 gap-1 font-mono text-[9px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
        {dayHeaders.map((d) => (
          <div key={d.key} className="px-1 pb-1 text-left">
            {d.label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weeks.flat().map((d) => {
          const iso = fmtYmd(d);
          const slots = slotsByDate.get(iso) ?? [];
          const inRange = d >= start && d <= end;
          const isToday = iso === todayIso;
          const isFirstOfMonth = d.getUTCDate() === 1;
          const monthLabel = isFirstOfMonth
            ? formatDate(d, { month: 'short', timeZone: 'UTC' }).toLowerCase()
            : null;

          const cellBase = 'flex h-14 flex-col rounded-md border px-1.5 py-1 text-left transition';
          const cellState = inRange
            ? slots.length > 0
              ? 'border-[var(--line)] bg-[var(--cream)]/40 hover:border-[var(--ink)]/40 hover:bg-[var(--cream)]/80 cursor-pointer'
              : 'border-[var(--line)]/60 bg-transparent'
            : 'border-[var(--line)]/30 bg-transparent opacity-40';
          const ringState = isToday
            ? 'ring-1 ring-[var(--plum)] ring-offset-1 ring-offset-[var(--paper)]'
            : '';

          if (slots.length > 0 && inRange) {
            return (
              <button
                key={iso}
                type="button"
                onClick={() => onDayClick(iso)}
                title={plural(slots.length, {
                  one: '# slot · click to jump',
                  other: '# slots · click to jump',
                })}
                className={`${cellBase} ${cellState} ${ringState}`}
              >
                <div className="flex items-baseline justify-between gap-1">
                  <span className="font-mono text-[10px] font-bold tabular-nums text-[var(--ink)]">
                    {d.getUTCDate()}
                  </span>
                  {monthLabel ? (
                    <span className="font-mono text-[8px] uppercase tracking-[0.08em] text-[var(--ink)]/50">
                      {monthLabel}
                    </span>
                  ) : null}
                </div>
                <div className="mt-auto flex flex-wrap gap-0.5">
                  {slots.slice(0, 4).map((s) => (
                    <span
                      key={s.slotId}
                      className={`inline-flex size-3.5 items-center justify-center rounded-[3px] font-mono text-[7px] font-bold text-[var(--paper)] ${chipColorFor(s.format)}`}
                      title={`${String(s.hour).padStart(2, '0')}:00 · ${s.format} · ${s.pillar}`}
                    >
                      {chipShortFor(s.format)}
                    </span>
                  ))}
                  {slots.length > 4 ? (
                    <span className="inline-flex size-3.5 items-center justify-center rounded-[3px] font-mono text-[7px] font-bold text-[var(--ink)]/70">
                      +{slots.length - 4}
                    </span>
                  ) : null}
                </div>
              </button>
            );
          }
          return (
            <div key={iso} className={`${cellBase} ${cellState} ${ringState}`}>
              <div className="flex items-baseline justify-between gap-1">
                <span className="font-mono text-[10px] tabular-nums text-[var(--ink)]/55">
                  {d.getUTCDate()}
                </span>
                {monthLabel ? (
                  <span className="font-mono text-[8px] uppercase tracking-[0.08em] text-[var(--ink)]/40">
                    {monthLabel}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Autopilot A5 — meta paid strategy panel. Founder clicks "what
 *  should i test next?" on a creative_ads campaign; mag reads
 *  recent metrics + brain + organic digest + emits 3-5 angle
 *  proposals (hook + audience hypothesis + format + suggested
 *  budget + rationale). Result lives at draft.paidStrategy so a
 *  refresh restores the panel. Re-running overwrites. ~$0.12/run. */
function PaidStrategyPanel({ campaign }: { campaign: CampaignDetail }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  const draft = campaign.draft;
  const strategy =
    draft && 'shape' in draft && draft.shape === 'plan' ? draft.paidStrategy : undefined;

  const onGenerate = async () => {
    setBusy(true);
    try {
      const result = await triggerMetaPaidStrategy(campaign.id);
      const cost = result.costUsd.toFixed(4);
      const angles = plural(result.angleCount, { one: '# angle', other: '# angles' });
      magToast.success(
        strategy ? t`re-strategized.` : t`strategy ready.`,
        t`${angles} · cost $${cost}`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't strategize.", founderErrorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  const formatLabel = (f: string): string => {
    if (f === 'static_image') return t`static`;
    if (f === 'reel') return t`reel`;
    if (f === 'video') return t`video`;
    return t`carousel`;
  };

  if (!strategy) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-[var(--ink)]/25 bg-[var(--paper)] p-5">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
              {'↘'} <Trans>what to test next</Trans>
            </p>
            <p className="mt-2 mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/65">
              <Trans>
                i can suggest a few new angles to try, based on how this has been doing and what i
                know about your brand.
              </Trans>
            </p>
          </div>
          <button
            type="button"
            onClick={onGenerate}
            disabled={busy}
            className="self-end shrink-0 rounded-md border border-[var(--plum)]/40 bg-[var(--plum)]/10 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)] transition hover:bg-[var(--plum)]/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t`writing…` : t`write me some options`}
          </button>
        </div>
      </div>
    );
  }

  const angleCount = strategy.angles.length;
  const strategyCost = strategy.costUsd.toFixed(4);
  return (
    <section className="mt-6 space-y-4">
      <div className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>what to test next · {angleCount} angles</Trans>
          </p>
          <button
            type="button"
            onClick={onGenerate}
            disabled={busy}
            className="self-end rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t`writing…` : t`write me new ones`}
          </button>
        </div>
        <p className="mt-2 font-serif text-[14px] leading-[1.5] text-[var(--ink)]/80">
          {strategy.summary}
        </p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
          <Trans>proposed · cost ${strategyCost}</Trans>
        </p>
      </div>

      <ul className="space-y-2">
        {strategy.angles.map((a) => (
          <li
            key={`${a.format}-${a.hook}`}
            className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
              <Trans>
                {formatLabel(a.format)} · ${a.suggestedDailyBudget}/day
              </Trans>
            </p>
            <p className="mt-1 font-serif text-[14px] leading-[1.5] text-[var(--ink)]">
              “{a.hook}”
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--plum)]">
              {'↘'} <Trans>audience</Trans>
            </p>
            <p className="mt-0.5 font-serif text-[13px] leading-[1.5] text-[var(--ink)]/75">
              {a.audienceHypothesis}
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--plum)]">
              {'↘'} <Trans>rationale</Trans>
            </p>
            <p className="mt-0.5 font-serif text-[12px] leading-[1.5] text-[var(--ink)]/70">
              {a.rationale}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Autopilot A6 — google paid strategy panel. Sibling of the meta
 *  panel: same UX, RSA-shaped angle cards (search intent +
 *  headlines + descriptions + keyword themes instead of hook +
 *  audience). Lives on campaign.draft.googlePaidStrategy. */
function GooglePaidStrategyPanel({ campaign }: { campaign: CampaignDetail }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  const draft = campaign.draft;
  const strategy =
    draft && 'shape' in draft && draft.shape === 'plan' ? draft.googlePaidStrategy : undefined;

  const onGenerate = async () => {
    setBusy(true);
    try {
      const result = await triggerGooglePaidStrategy(campaign.id);
      const cost = result.costUsd.toFixed(4);
      const angles = plural(result.angleCount, { one: '# angle', other: '# angles' });
      magToast.success(
        strategy ? t`re-strategized.` : t`google strategy ready.`,
        t`${angles} · cost $${cost}`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't strategize.", founderErrorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  if (!strategy) {
    return (
      /* No mt-6: this panel is a child of MagRecommends now, and the
         container owns the spacing. Carrying its own margin gave the block
         two competing rhythms. */
      <div className="rounded-2xl border border-dashed border-[var(--ink)]/25 bg-[var(--paper)] p-5">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
              {'↘'} <Trans>other wording to try</Trans>
            </p>
            {/* "next RSA angles to test — search intents, headlines, keyword
                themes" was four pieces of trade vocabulary in one sentence,
                inside the block spec 5614cfab C-1 exists to keep clear of it.
                RSA was not on C-1's ban list, which is how it got here; the
                list is longer now. */}
            <p className="mt-2 mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/65">
              <Trans>
                i can write you a few different versions of this ad to try — different angles on
                what you offer, in case one lands better than what's running.
              </Trans>
            </p>
          </div>
          <button
            type="button"
            onClick={onGenerate}
            disabled={busy}
            className="self-end shrink-0 rounded-md border border-[var(--plum)]/40 bg-[var(--plum)]/10 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)] transition hover:bg-[var(--plum)]/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t`writing…` : t`write me some options`}
          </button>
        </div>
      </div>
    );
  }

  const angleCount = strategy.angles.length;
  const strategyCost = strategy.costUsd.toFixed(4);
  return (
    <section className="mt-6 space-y-4">
      <div className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>google: what to test next · {angleCount} angles</Trans>
          </p>
          <button
            type="button"
            onClick={onGenerate}
            disabled={busy}
            className="self-end rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t`writing…` : t`write me new ones`}
          </button>
        </div>
        <p className="mt-2 font-serif text-[14px] leading-[1.5] text-[var(--ink)]/80">
          {strategy.summary}
        </p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
          <Trans>proposed · cost ${strategyCost}</Trans>
        </p>
      </div>

      <ul className="space-y-2">
        {strategy.angles.map((a) => (
          <li
            key={a.searchIntent}
            className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
              <Trans>search intent · ${a.suggestedDailyBudget}/day</Trans>
            </p>
            <p className="mt-1 font-serif text-[14px] leading-[1.5] text-[var(--ink)]">
              {a.searchIntent}
            </p>

            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--plum)]">
              {'↘'} <Trans>headlines ({a.headlines.length})</Trans>
            </p>
            <ul className="mt-1 space-y-0.5">
              {a.headlines.map((h) => (
                <li key={h} className="font-serif text-[13px] leading-[1.4] text-[var(--ink)]/85">
                  · {h}
                </li>
              ))}
            </ul>

            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--plum)]">
              {'↘'} <Trans>descriptions ({a.descriptions.length})</Trans>
            </p>
            <ul className="mt-1 space-y-0.5">
              {a.descriptions.map((d) => (
                <li key={d} className="font-serif text-[13px] leading-[1.45] text-[var(--ink)]/75">
                  · {d}
                </li>
              ))}
            </ul>

            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--plum)]">
              {'↘'} <Trans>keyword themes ({a.keywordThemes.length})</Trans>
            </p>
            <ul className="mt-1 space-y-0.5">
              {a.keywordThemes.map((kt) => (
                <li key={kt} className="font-mono text-[11px] leading-[1.45] text-[var(--ink)]/75">
                  · {kt}
                </li>
              ))}
            </ul>

            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--plum)]">
              {'↘'} <Trans>rationale</Trans>
            </p>
            <p className="mt-0.5 font-serif text-[12px] leading-[1.5] text-[var(--ink)]/70">
              {a.rationale}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** cap2 — per-day rolling paid schedule panel. Strategy says WHAT
 *  to test; schedule says WHEN at what budget. Founder triggers
 *  a draft per channel; the daily applier scheduler pushes today's
 *  row to the platform on each tick once stored. Schema caps at
 *  14 days; we currently emit 7 per pass. Stale (>14d) schedules
 *  show a re-draft nudge — the applier skips them anyway, so
 *  surfacing it lets the founder rebuild before tomorrow's tick. */
function PaidSchedulePanel({
  campaign,
  channel,
}: {
  campaign: CampaignDetail;
  channel: 'meta_paid' | 'google_paid';
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  const draft = campaign.draft;
  const schedule =
    draft && 'shape' in draft && draft.shape === 'plan'
      ? channel === 'meta_paid'
        ? draft.paidSchedule
        : draft.googlePaidSchedule
      : undefined;

  const channelLabel = channel === 'meta_paid' ? 'meta' : 'google';

  const onGenerate = async () => {
    setBusy(true);
    try {
      const result = await triggerPaidScheduleDraft(campaign.id, channel);
      const cost = result.costUsd.toFixed(4);
      const days = result.daysGenerated;
      magToast.success(
        schedule ? t`re-scheduled.` : t`schedule ready.`,
        t`${days} days · cost $${cost}`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't schedule.", founderErrorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  if (!schedule) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--ink)]/25 bg-[var(--paper)] p-5">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
              {'↘'} <Trans>spend more on your best days</Trans>
            </p>
            <p className="mt-2 mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/65">
              <Trans>
                your daily budget is the same every day right now. i can work out which days
                actually bring people in and shift more of it there.
              </Trans>
            </p>
          </div>
          <button
            type="button"
            onClick={onGenerate}
            disabled={busy}
            title={t`i'll draft a day-by-day plan for the next week. costs about 10 cents.`}
            className="self-end shrink-0 rounded-md border border-[var(--plum)]/40 bg-[var(--plum)]/10 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)] transition hover:bg-[var(--plum)]/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t`working it out…` : t`plan my week`}
          </button>
        </div>
      </div>
    );
  }

  const dayCount = schedule.days.length;
  const scheduleCost = schedule.costUsd.toFixed(4);
  const generatedDate = new Date(schedule.generatedAt);
  const ageDays = Math.floor((Date.now() - generatedDate.getTime()) / (24 * 60 * 60 * 1000));
  const stale = ageDays > 14;
  // ageDays === 0 is "today", which Intl words for us with numeric 'auto'.
  const draftedAgo = formatRelativeTimeCompact(generatedDate);
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <section className="mt-6 space-y-4">
      <div
        className={`rounded-xl border bg-[var(--paper)] p-4 ${stale ? 'border-[var(--pulse)]/40' : 'border-[var(--line)]'}`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'}{' '}
            <Trans>
              {channelLabel}: 7-day budget plan · {dayCount} days
            </Trans>
          </p>
          <button
            type="button"
            onClick={onGenerate}
            disabled={busy}
            title={
              stale
                ? t`schedule is stale (>14d). applier is skipping it. ask mag to re-draft.`
                : t`overwrites the existing schedule. cost ~$0.10.`
            }
            className="self-end rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t`re-scheduling…` : stale ? t`re-draft (stale)` : t`re-draft`}
          </button>
        </div>
        <p className="mt-2 font-serif text-[14px] leading-[1.5] text-[var(--ink)]/80">
          {schedule.summary}
        </p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
          <Trans>drafted · cost ${scheduleCost}</Trans> {'·'} {draftedAgo}
          {stale ? <Trans> · stale — applier paused</Trans> : null}
        </p>
      </div>

      <ul className="space-y-2">
        {schedule.days.map((d) => {
          const isToday = d.date === todayIso;
          const dayTotal = d.perDeliverable.reduce((s, p) => s + p.dailyBudgetUsd, 0);
          return (
            <li
              key={d.date}
              className={`rounded-xl border bg-[var(--paper)] p-4 ${isToday ? 'border-[var(--plum)]/60 ring-1 ring-[var(--plum)]/20' : 'border-[var(--line)]'}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55">
                  {d.date}
                  {isToday ? <Trans> · today</Trans> : ''}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
                  <Trans>${dayTotal}/day</Trans>
                </p>
              </div>
              <p className="mt-1 font-serif text-[13px] leading-[1.5] text-[var(--ink)]/80">
                {d.dayRationale}
              </p>
              {d.perDeliverable.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {d.perDeliverable.map((p) => (
                    <li
                      key={p.deliverableId}
                      className="flex items-baseline gap-3 font-mono text-[11px] text-[var(--ink)]/70"
                    >
                      <span className="w-[60px] shrink-0 font-semibold">${p.dailyBudgetUsd}</span>
                      <span className="min-w-0 flex-1 truncate font-serif text-[12px] text-[var(--ink)]/65">
                        {p.rationale}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
                  <Trans>day off · applier keeps ads at platform floor ($1)</Trans>
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Post-launch deliverables surface (s183). Renders the campaign's
 *  plan deliverables — including Instagram posts with active publish
 *  buttons — so the founder can ship them after launch. Replaces the
 *  old OtherKindTombstone for non-analytics campaigns. Analytics
 *  campaigns keep their own surface (InsightsPanel) for now; s184
 *  will fold analytics deliverables in here too once the IG arc
 *  settles. */
/** @param slot which half of this section to render.
 *
 *  Spec 5614cfab slice 1 wants the health evidence inside the "why i say
 *  that" disclosure and the ad itself out in the open, but both are
 *  produced by one handler-dense component (~360 lines of mutations that
 *  the two halves genuinely share — a dead-keyword pause and an ad launch
 *  both invalidate the same campaign query). Splitting it properly is the
 *  "heavier per-row tooling" the :157 comment warned about and is its own
 *  commit.
 *
 *  So the component renders twice, once per slot. The duplicated local
 *  state is harmless — each instance owns different buttons — and the
 *  queries are the same cache keys, so the second mount costs a render,
 *  not a fetch. Default 'both' keeps any future caller unchanged. */
/** The launch button at the TOP of the detail page.
 *
 *  Its own tiny component so it can call the shared hook: the rail renders
 *  inside CampaignDetailsPage, and the only other caller lives several
 *  hundred lines down in PlanDeliverablesSection. A node built inline in the
 *  page would have needed the hook lifted into it, which puts launch state on
 *  a component that re-renders for every unrelated reason on this page.
 *
 *  Says WHERE the ad is, not just what the button does — "not on google yet"
 *  is the fact the founder is missing, and the header pill alone ("NOT LIVE
 *  YET") never said what to do about it. */
function LaunchGoogleAdAction({
  campaign,
  deliverableId,
}: {
  campaign: CampaignDetail;
  deliverableId: string;
}) {
  const { t } = useLingui();
  const { launch, launchingId } = useLaunchGoogleSearchAd(campaign);
  const isLaunching = launchingId === deliverableId;

  return (
    <div className="rounded-xl border-[1.5px] border-[var(--ink)] bg-[var(--spell)] p-4">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/60">
        {'↘'} <Trans>not on google yet</Trans>
      </p>
      <p className="mt-2 font-serif text-[13px] leading-snug text-[var(--ink)]">
        <Trans>this ad is written and waiting. nothing runs until you push it.</Trans>
      </p>
      {/* Right-aligned, per the project's action-button rule. */}
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={() => void launch(deliverableId)}
          disabled={isLaunching}
          className="btn disabled:opacity-60"
        >
          {isLaunching ? t`launching…` : t`push to google`}
          <span className="btn-arrow">{'→'}</span>
        </button>
      </div>
    </div>
  );
}

/** Launching a google_search_ad, shared by the two places that offer it.
 *
 *  ONE IMPLEMENTATION, TWO CALL SITES, deliberately. The button used to live
 *  only at the foot of the ad block, below the keyword lists, and a founder
 *  reported missing it entirely (2026-10-09). The rail now offers the same
 *  action at the top of the page while the campaign is not live — and the way
 *  to add a second entry point without the two drifting is to share the
 *  handler rather than copy twenty lines of toast-and-refresh.
 *
 *  Owns its own pending id rather than the section's shared `publishingId`,
 *  which also tracks instagram publishes and meta launches: with one id, a
 *  launch started from the rail would have spun the spinner on an unrelated
 *  deliverable's button down the page. */
function useLaunchGoogleSearchAd(campaign: CampaignDetail) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { t } = useLingui();
  const [launchingId, setLaunchingId] = useState<string | null>(null);

  const launch = async (deliverableId: string) => {
    setLaunchingId(deliverableId);
    try {
      const updated = await publishDeliverable(campaign.id, deliverableId);
      const refreshed =
        updated.draft && 'shape' in updated.draft && updated.draft.shape === 'plan'
          ? (updated.draft.deliverables.find(
              (d) => d.kind === 'google_search_ad' && d.id === deliverableId,
            ) as GoogleSearchAdDeliverable | undefined)
          : undefined;
      const wasReal = refreshed?.launched_via === 'google';
      magToast.success(
        wasReal ? t`launched on google ads.` : t`created — pending real launch.`,
        wasReal
          ? t`it's live in your google ads account. mag will start reading metrics on the next scan.`
          : // DOES NOT NAME A CAUSE ANY MORE. This used to read "connect a
            // google ads account at /channels", which was wrong for the one
            // founder who hit it: their account WAS connected, and the real
            // blocker was a domain-authorization refusal the api swallowed
            // into this mock path. The api now throws that refusal instead
            // (DeliverablePublishError 'domain_not_authorized'), so a mock
            // here means the platform was genuinely unreachable — and this
            // copy no longer guesses which.
            t`i created the ad on our side — it is not on google yet. check your google ads connection at /channels, then try again.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't launch on google.", founderErrorCopy(err));
    } finally {
      setLaunchingId(null);
    }
  };

  return { launch, launchingId };
}

function PlanDeliverablesSection({
  campaign,
  slot = 'both',
  bidsSuppressedReason,
}: {
  campaign: CampaignDetail;
  /** [spec one-campaign-page-one-answer, D2] Passed straight through to
   *  CampaignHealthPanel: why the page's one answer outranked the bid
   *  findings, when it did. The findings keep rendering; their buttons
   *  do not. */
  bidsSuppressedReason?: string | null | undefined;
  slot?: 'health' | 'deliverables' | 'both';
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [generatingImageId, setGeneratingImageId] = useState<string | null>(null);
  const [refreshingMetricsId, setRefreshingMetricsId] = useState<string | null>(null);
  const [unpausingDeliverableId, setUnpausingDeliverableId] = useState<string | null>(null);
  const [pausingDeliverableId, setPausingDeliverableId] = useState<string | null>(null);
  const [approvingAutodraftId, setApprovingAutodraftId] = useState<string | null>(null);
  const [checkingStatusId, setCheckingStatusId] = useState<string | null>(null);
  const [healthActionId, setHealthActionId] = useState<string | null>(null);
  // Declared here (not next to the launch-all handler below) so it sits
  // above the early returns — hooks must run in the same order every render.
  const [launchingAll, setLaunchingAll] = useState(false);
  // s205 — google_search_ad launch goes through the same publish
  // endpoint as the meta + ig kinds. The api dispatches by kind →
  // RealGoogleAdsClient.createCampaign (5-step chain) or mock.
  //
  // Called here for the same reason as launchingAll above: this is a hook
  // now, not the plain async function it used to be, and the launch-all
  // handler it used to sit beside is below two early returns.
  const { launch: onLaunchGoogleSearchAd, launchingId: launchingGoogleId } =
    useLaunchGoogleSearchAd(campaign);
  // Which ad platforms the brand has actually connected. Used to gate
  // the per-ad launch handlers — we don't expose a "launch on google"
  // button when there's no connected google ads account, because the
  // launch would just mock again. Tiles render a "connect at /channels"
  // hint instead.
  const adAccountsQ = useQuery(adAccountsQuery(campaign.brandId));
  const connectedPlatforms = new Set<'meta' | 'google'>(
    (adAccountsQ.data?.accounts ?? [])
      .filter((a) => a.status === 'connected')
      .map((a) => a.provider)
      .filter((p): p is 'meta' | 'google' => p === 'meta' || p === 'google'),
  );
  // The account a launched google ad actually lives in, for the
  // "open in google ads" deep link on each launched tile. Null when the
  // brand has no connected google account — the tile then prints the
  // campaign id unlinked rather than sending the founder to an account
  // picker for an account they do not have.
  const googleCustomerId =
    (adAccountsQ.data?.accounts ?? []).find(
      (a) => a.provider === 'google' && a.status === 'connected',
    )?.providerCustomerId ?? null;
  const plan = useMemo(
    () => (campaign.draft ? draftToPlanView(campaign.draft) : null),
    [campaign.draft],
  );
  if (!plan) {
    return <OtherKindTombstone />;
  }
  const paidHooks = plan.deliverables.filter(
    (d) => d.kind === 'paid_ad_hook',
  ) as PaidAdHookDeliverable[];
  const analyticsEvents = plan.deliverables.filter(
    (d) => d.kind === 'analytics_event',
  ) as AnalyticsEventDeliverable[];
  const instagramPosts = plan.deliverables.filter(
    (d) => d.kind === 'instagram_post',
  ) as InstagramPostDeliverable[];
  const metaPaidAds = plan.deliverables.filter(
    (d) => d.kind === 'meta_paid_ad',
  ) as MetaPaidAdDeliverable[];
  const googleSearchAds = plan.deliverables.filter(
    (d) => d.kind === 'google_search_ad',
  ) as GoogleSearchAdDeliverable[];

  const total =
    paidHooks.length +
    analyticsEvents.length +
    instagramPosts.length +
    metaPaidAds.length +
    googleSearchAds.length;
  if (total === 0) return <OtherKindTombstone />;

  const onPublishInstagramPost = async (deliverableId: string) => {
    setPublishingId(deliverableId);
    try {
      const updated = await publishDeliverable(campaign.id, deliverableId);
      // The response carries the via field on the deliverable so we
      // can tell mock from real in the toast copy.
      const refreshed =
        updated.draft && 'shape' in updated.draft && updated.draft.shape === 'plan'
          ? (updated.draft.deliverables.find(
              (d) => d.kind === 'instagram_post' && d.id === deliverableId,
            ) as InstagramPostDeliverable | undefined)
          : undefined;
      const wasReal = refreshed?.published_via === 'meta';
      magToast.success(
        wasReal ? t`shipped to instagram.` : t`mock-published.`,
        wasReal
          ? t`it's live on instagram now.`
          : t`the deliverable's marked shipped on this campaign.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't publish.", founderErrorCopy(err));
    } finally {
      setPublishingId(null);
    }
  };

  const onGenerateInstagramImage = async (deliverableId: string) => {
    setGeneratingImageId(deliverableId);
    try {
      await generateDeliverableImage(campaign.id, deliverableId);
      magToast.success(t`image rendered.`, t`attached to the deliverable.`);
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't render image.", founderErrorCopy(err));
    } finally {
      setGeneratingImageId(null);
    }
  };

  // s193 — meta_paid_ad launch goes through the same publish
  // endpoint as instagram_post; the api dispatches by kind. Mock vs
  // real readback is on launched_via, mirroring published_via on
  // the IG side.
  const onLaunchMetaPaidAd = async (deliverableId: string) => {
    setPublishingId(deliverableId);
    try {
      const updated = await publishDeliverable(campaign.id, deliverableId);
      const refreshed =
        updated.draft && 'shape' in updated.draft && updated.draft.shape === 'plan'
          ? (updated.draft.deliverables.find(
              (d) => d.kind === 'meta_paid_ad' && d.id === deliverableId,
            ) as MetaPaidAdDeliverable | undefined)
          : undefined;
      const wasReal = refreshed?.launched_via === 'meta';
      magToast.success(
        wasReal ? t`launched on meta.` : t`created — pending real launch.`,
        wasReal
          ? t`it's in your ads manager as PAUSED. activate it there when you're ready to spend.`
          : t`i created the ad on our side but no live push — you're in dev/mock mode. connect a meta ad account at /channels to ship for real.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't launch on meta.", founderErrorCopy(err));
    } finally {
      setPublishingId(null);
    }
  };

  // Autopilot A3b-finish — founder-driven unpause for autopilot-
  // paused paid deliverables. Calls the api which clears the audit
  // fields + resumes the campaign on Meta/Google's side.
  const onUnpauseDeliverable = async (deliverableId: string) => {
    setUnpausingDeliverableId(deliverableId);
    try {
      await unpauseDeliverable(campaign.id, deliverableId);
      magToast.success(
        t`resumed.`,
        t`mag will keep watching — if it stays flat she may pause again.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't resume.", founderErrorCopy(err));
    } finally {
      setUnpausingDeliverableId(null);
    }
  };

  // Founder-initiated pause. The api could UNPAUSE a deliverable the
  // founder had no way to pause: the only founder-initiated pause was
  // asking mag in chat. The platform dispatch and the founder-pause
  // fields already existed — this is the route and the button.
  //
  // platformDispatched is reported rather than assumed. A brand with no
  // live connection still gets its local state stamped, which is right,
  // but saying "paused on google" when nothing reached google is the
  // same class of lie as a delete that leaves the ad running.
  const onPauseDeliverable = async (deliverableId: string) => {
    setPausingDeliverableId(deliverableId);
    try {
      const { platformDispatched } = await pauseDeliverable(campaign.id, deliverableId);
      magToast.success(
        platformDispatched ? t`paused.` : t`paused here — not on the platform.`,
        platformDispatched
          ? t`it's stopped serving. resume whenever you want.`
          : t`there's no live ad account connected, so mag couldn't reach it. connect one at /channels, then pause again.`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't pause.", founderErrorCopy(err));
    } finally {
      setPausingDeliverableId(null);
    }
  };

  // "check now" — ask google, this second, whether the ad is serving.
  //
  // The automatic reading lands on the brand-coach tick (1-4h), which is
  // a schedule the founder cannot see and should not have to wait for
  // while watching a campaign clear review. Reports the answer in mag's
  // words rather than echoing google's enum at them.
  const onCheckGoogleAdStatus = async (deliverableId: string) => {
    setCheckingStatusId(deliverableId);
    try {
      const updated = await refreshGoogleSearchAdStatus(campaign.id, deliverableId);
      const state = deliverableServingState(
        { id: deliverableId, launched_at: new Date().toISOString(), launched_via: 'google' },
        updated.platformServingStatus,
      );
      // Hoisted: BOTH the pending and the limited branch need it. It used to
      // live inside the pending branch, which is how the limited case ended
      // up telling the founder to go read the reason in google's ads manager
      // while we were holding it.
      const phrase = servingReasonPhrase(
        Object.values(updated.platformServingStatus ?? {}).flatMap((r) => r.reasons),
      );
      if (state === 'serving') {
        magToast.success(t`it's serving.`, t`google is showing your ad.`);
      } else if (state === 'pending') {
        magToast.success(
          t`not serving yet.`,
          phrase
            ? t`${phrase}. i'll keep checking.`
            : t`google hasn't started serving this yet. i'll keep checking.`,
        );
      } else if (state === 'limited') {
        // `phrase` is computed above from the reasons google already sent us.
        // This branch used to ignore it and send the founder to the ads
        // manager to read a string we were holding — mag is the abstraction
        // layer, so she says it.
        magToast.success(
          t`serving, but limited.`,
          phrase
            ? t`${phrase} — that's what's holding it back.`
            : t`google is running it with restrictions but didn't say which. i'll keep checking.`,
        );
      } else {
        magToast.success(t`checked.`, t`google didn't give me a clear answer on this one.`);
      }
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't reach google.", founderErrorCopy(err));
    } finally {
      setCheckingStatusId(null);
    }
  };

  // Spec 3c98725c — the two fixes the health panel offers. The api
  // re-reads from google after each write, so the response carries the
  // real new state rather than an optimistic one.
  const runHealthAction = async (
    deliverableId: string,
    action:
      | { action: 'raise_bid'; cpcBidUsd: number }
      | { action: 'pause_keyword'; criterionId: string },
    successTitle: string,
    successBody: string,
  ) => {
    setHealthActionId(deliverableId);
    try {
      await applyCampaignHealthAction(campaign.id, deliverableId, action);
      magToast.success(successTitle, successBody);
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't apply that on google.", founderErrorCopy(err));
    } finally {
      setHealthActionId(null);
    }
  };

  // A6-finish — approve an autodrafted google_search_ad. Clears
  // autopilot_drafted_at; A4 google autolaunch picks it up on the
  // next tick.
  const onApproveAutodraft = async (deliverableId: string) => {
    setApprovingAutodraftId(deliverableId);
    try {
      await approveAutodraft(campaign.id, deliverableId);
      magToast.success(
        t`approved — launching soon.`,
        t`i'll ship it within the next google autolaunch tick (~5 min).`,
      );
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't approve.", founderErrorCopy(err));
    } finally {
      setApprovingAutodraftId(null);
    }
  };

  const onRefreshMetaPaidAdMetrics = async (deliverableId: string) => {
    setRefreshingMetricsId(deliverableId);
    try {
      await refreshMetaPaidAdMetrics(campaign.id, deliverableId);
      magToast.success(t`metrics refreshed.`, t`snapshot from meta is current.`);
      // s197 — the refresh appends a row to history, so the trend
      // chart needs to refetch too.
      await refreshQueries(
        queryClient,
        ['campaign', campaign.id],
        ['meta-paid-ad-metrics-history', campaign.id, deliverableId],
      );
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't refresh metrics.", founderErrorCopy(err));
    } finally {
      setRefreshingMetricsId(null);
    }
  };

  // Sequentially publish every paid deliverable that's ready to ship to
  // a connected platform. "Ready" = not real-launched AND its platform
  // has a connected ad account. Mock-launched ads are eligible for
  // re-launch now that the api guard treats launched_via='mock' as
  // unshipped. Ads on un-connected platforms are excluded — they'd
  // just mock-launch again, which isn't useful for a batch button.
  const launchablePaidAds = [...metaPaidAds, ...googleSearchAds].filter((d) => {
    const isUnshipped = !d.launched_at || d.launched_via === 'mock';
    if (!isUnshipped) return false;
    if (d.kind === 'google_search_ad') return connectedPlatforms.has('google');
    return connectedPlatforms.has('meta');
  });
  const onLaunchAll = async () => {
    if (launchablePaidAds.length === 0) return;
    setLaunchingAll(true);
    let okCount = 0;
    let failCount = 0;
    for (const d of launchablePaidAds) {
      setPublishingId(d.id);
      try {
        await publishDeliverable(campaign.id, d.id);
        okCount += 1;
      } catch (err) {
        failCount += 1;
        console.error('[launch-all] failed for', d.id, err);
      }
    }
    setPublishingId(null);
    await refreshQueries(queryClient, ['campaign', campaign.id]);
    await router.invalidate();
    setLaunchingAll(false);
    if (failCount === 0) {
      magToast.success(
        plural(okCount, { one: 'launched # ad.', other: 'launched # ads.' }),
        t`each ad ran through the platform pipeline. open them to see status.`,
      );
    } else if (okCount === 0) {
      magToast.error(
        plural(failCount, {
          one: "couldn't launch the ad.",
          other: "couldn't launch any of the # ads.",
        }),
        t`check the per-ad cards below for the failure reason.`,
      );
    } else {
      const attempted = okCount + failCount;
      magToast.success(
        t`launched ${okCount} of ${attempted}.`,
        plural(failCount, {
          one: '# failed — check the per-ad card below.',
          other: '# failed — check the per-ad cards below.',
        }),
      );
    }
  };

  const showHealth = slot === 'health' || slot === 'both';
  const showDeliverables = slot === 'deliverables' || slot === 'both';

  return (
    <div className={slot === 'both' ? 'mt-8' : ''}>
      {showHealth && launchablePaidAds.length >= 1 ? (
        <LaunchAllBar
          count={launchablePaidAds.length}
          busy={launchingAll}
          onLaunchAll={onLaunchAll}
        />
      ) : null}
      {showHealth
        ? launchedPaidDeliverables(campaign)
            .filter((d) => d.id !== undefined)
            .map((d) => {
              const deliverableId = d.id as string;
              return (
                <div key={`health-wrap-${deliverableId}`} className="flex flex-col gap-1">
                  <CampaignGeoLine campaignId={campaign.id} deliverableId={deliverableId} />
                  <CampaignAssetsLine coverage={campaign.assetCoverage?.[deliverableId]} />
                  <CampaignHealthPanel
                    report={campaign.campaignHealth?.[deliverableId]?.report}
                    keywords={campaign.campaignHealth?.[deliverableId]?.keywords}
                    servingVerdict={campaign.campaignVerdicts?.[deliverableId]}
                    deliverableLabel={deliverableId}
                    bidsSuppressedReason={bidsSuppressedReason}
                    busy={healthActionId === deliverableId}
                    onRaiseBid={(f) =>
                      runHealthAction(
                        deliverableId,
                        { action: 'raise_bid', cpcBidUsd: f.firstPageCpcUsd },
                        t`bid raised.`,
                        t`max cpc is $${f.firstPageCpcUsd.toFixed(2)} now — google should start showing you on the first page.`,
                      )
                    }
                    onPauseKeyword={(f) =>
                      runHealthAction(
                        deliverableId,
                        { action: 'pause_keyword', criterionId: f.criterionId },
                        t`keyword paused.`,
                        t`“${f.keyword}” is paused, not deleted — flip it back any time.`,
                      )
                    }
                    // SAME ACTION, DIFFERENT SENTENCE. A dead keyword pauses through
                    // the same criterionId path as an underbid one — the panel keeps
                    // the handlers separate only because the finding TYPES differ
                    // (a dead keyword carries no bid fields).
                    //
                    // Without this prop DeadKeywordBlock renders its rows with no
                    // pause button at all, so the founder read "pausing them puts the
                    // budget behind the terms that do serve" and had nothing to press.
                    // The panel was plumbed for this from the start; only the caller
                    // was missing.
                    onPauseDeadKeyword={(f) =>
                      runHealthAction(
                        deliverableId,
                        { action: 'pause_keyword', criterionId: f.criterionId },
                        t`keyword paused.`,
                        t`“${f.keyword}” is paused — there was no auction to win on it, so the budget now sits behind the terms that do serve.`,
                      )
                    }
                  />
                  <CampaignSearchTerms campaignId={campaign.id} deliverableId={deliverableId} />
                </div>
              );
            })
        : null}
      {showDeliverables ? (
        <DeliverablesPanel
          // The campaign page's main column is a gap-6 flex column and owns the
          // rhythm; the review screen stacks these directly and keeps its mt-6.
          ownsSpacing={slot === 'both'}
          {...(campaign.platformServingStatus
            ? { servingStatus: campaign.platformServingStatus }
            : {})}
          onCheckGoogleAdStatus={onCheckGoogleAdStatus}
          checkingStatusDeliverableId={checkingStatusId}
          paidHooks={paidHooks}
          analyticsEvents={analyticsEvents}
          instagramPosts={instagramPosts}
          metaPaidAds={metaPaidAds}
          googleSearchAds={googleSearchAds}
          googleCustomerId={googleCustomerId}
          campaignName={plan.name}
          tracker={null}
          onPublishInstagramPost={onPublishInstagramPost}
          publishingDeliverableId={publishingId}
          onGenerateInstagramImage={onGenerateInstagramImage}
          generatingImageDeliverableId={generatingImageId}
          {...(connectedPlatforms.has('meta') ? { onLaunchMetaPaidAd } : {})}
          launchingDeliverableId={publishingId}
          onGenerateMetaPaidAdImage={onGenerateInstagramImage}
          generatingMetaPaidAdImageId={generatingImageId}
          onRefreshMetaPaidAdMetrics={onRefreshMetaPaidAdMetrics}
          refreshingMetricsDeliverableId={refreshingMetricsId}
          trendCampaignId={campaign.id}
          {...(connectedPlatforms.has('google') ? { onLaunchGoogleSearchAd } : {})}
          launchingGoogleSearchAdId={launchingGoogleId}
          onApproveAutodraft={onApproveAutodraft}
          approvingAutodraftId={approvingAutodraftId}
          onUnpauseDeliverable={onUnpauseDeliverable}
          unpausingDeliverableId={unpausingDeliverableId}
          onPauseDeliverable={onPauseDeliverable}
          pausingDeliverableId={pausingDeliverableId}
        />
      ) : null}
    </div>
  );
}

/** Per-campaign creative drafts. Mag uses the campaign's identity
 *  (brief, short pitch, draft positioning) to pre-fill what she'll
 *  design — the founder reviews, edits the brief if they want, and
 *  hits "draft creative." Drafts attach to the campaign via
 *  campaign_id so the launch flow (s161) can pull them when shipping
 *  to Meta. Visible for every campaign kind, including analytics_setup
 *  (mag can design educational posts about the tracking setup, too). */
function CampaignCreativeSection({ campaign }: { campaign: CampaignDetail }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  // brandId on the campaign row is the source of truth for the
  // creative library scope. The pipeline + the listing both key off
  // it.
  const brandsQ = useQuery(brandsQuery());
  const brand = brandsQ.data?.find((b) => b.id === campaign.brandId) ?? null;

  // Pre-filled brief: mag has already done her thinking during the
  // intake / draft phase, so we hand her own pitch back as the
  // starting point. The founder can edit before tapping draft —
  // matches the "mag operates on her own; founder approves" rhythm.
  const initialBrief = useMemo(() => composeCampaignBrief(campaign), [campaign]);
  const [brief, setBrief] = useState(initialBrief);
  const [formats, setFormats] = useState<CreativeAssetFormat[]>(['feed_1x1']);
  const [openAssetId, setOpenAssetId] = useState<string | null>(null);

  const assetsQ = useQuery({
    ...campaignCreativeAssetsQuery(campaign.brandId, campaign.id),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      const anyPending = data.creativeAssets.some(
        (a) => a.status === 'pending' || a.status === 'rendering',
      );
      return anyPending ? 4_000 : false;
    },
  });
  const assets = assetsQ.data?.creativeAssets ?? [];
  const items = useMemo(() => groupCreativeAssets(assets), [assets]);
  const openAsset = useMemo(
    () => assets.find((a) => a.id === openAssetId) ?? null,
    [assets, openAssetId],
  );
  const openCarouselSlides = useMemo(() => {
    if (!openAsset?.carouselGroupId) return undefined;
    return assets
      .filter((a) => a.carouselGroupId === openAsset.carouselGroupId)
      .sort((a, b) => (a.slideIndex ?? 0) - (b.slideIndex ?? 0));
  }, [assets, openAsset]);

  const [pendingClarification, setPendingClarification] = useState<{
    question: string;
    originalBrief: string;
  } | null>(null);
  const [clarificationAnswer, setClarificationAnswer] = useState('');

  const drafting = useMutation({
    mutationFn: async (overrideBrief?: string) => {
      const text = (overrideBrief ?? brief).trim();
      if (!text) throw new Error('brief is empty');
      return runCreativePipeline(campaign.brandId, {
        brief: text,
        formats,
        campaignId: campaign.id,
      });
    },
    onSuccess: async (result, sentBrief) => {
      if (result.kind === 'clarification') {
        setPendingClarification({
          question: result.question,
          originalBrief: (sentBrief as string | undefined) ?? brief,
        });
        return;
      }
      await refreshQueries(queryClient, ['creative-assets']);
      magToast.success(
        t`mag drafted it.`,
        plural(result.creativeAssetIds.length, {
          one: '# render queued for this campaign.',
          other: '# renders queued for this campaign.',
        }),
      );
      setPendingClarification(null);
      setClarificationAnswer('');
    },
    onError: (err) => {
      magToast.error("mag couldn't draft.", founderErrorCopy(err));
    },
  });

  const handleClarificationAnswer = (e: FormEvent) => {
    e.preventDefault();
    if (drafting.isPending || !pendingClarification) return;
    const answer = clarificationAnswer.trim();
    if (!answer) return;
    const merged = `${pendingClarification.originalBrief}\n\nMAG ASKED: ${pendingClarification.question}\nFOUNDER ANSWERED: ${answer}`;
    drafting.mutate(merged);
  };

  const toggleFormat = (f: CreativeAssetFormat) => {
    setFormats((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (drafting.isPending) return;
    drafting.mutate(undefined);
  };

  const handleResetBrief = () => setBrief(initialBrief);

  if (!brand) return null;

  return (
    <section className="mt-10">
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>creative</Trans>
      </p>
      <h2 className="mt-1 font-display text-[22px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--ink)]">
        <Trans>what mag's drafting for this campaign</Trans>
      </h2>
      <p className="mt-2 max-w-[680px] text-[14px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>
          mag pre-filled the brief from the campaign you briefed her on. tweak it if you want — or
          send it as-is and she'll design against your brand visuals.
        </Trans>
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-5 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-4 shadow-[4px_4px_0_var(--spell)]"
      >
        <div className="flex items-start gap-3">
          <Mag size={32} mood="casting" />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
                {'↘'} <Trans>mag's brief for this campaign</Trans>
              </span>
              {brief !== initialBrief ? (
                <button type="button" onClick={handleResetBrief} className="link-quiet text-[11px]">
                  {'↘'} <Trans>reset to mag's</Trans>
                </button>
              ) : null}
            </div>
            <label className="field mt-1">
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={4}
                disabled={drafting.isPending}
                maxLength={2000}
              />
            </label>
            <div className="mt-3">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
                {'↘'} <Trans>formats</Trans>
              </span>
              <div className="mt-2 flex flex-wrap gap-2">
                {campaignFormats(t).map((f) => {
                  const active = formats.includes(f.value);
                  return (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => toggleFormat(f.value)}
                      disabled={drafting.isPending}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] transition disabled:opacity-50 ${
                        active
                          ? 'border-[1.5px] border-[var(--ink)] bg-[var(--spell)] font-bold shadow-[2px_2px_0_var(--ink)]'
                          : 'border-[1.5px] border-[var(--line-strong)] bg-white font-medium text-[var(--ink)]/75'
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end">
              <button
                type="submit"
                disabled={drafting.isPending || formats.length === 0 || !brief.trim()}
                className="btn disabled:opacity-60"
              >
                {drafting.isPending ? t`mag is drafting…` : t`ask mag to draft`}
                <span className="btn-arrow">→</span>
              </button>
            </div>
          </div>
        </div>
      </form>

      {pendingClarification ? (
        <form
          onSubmit={handleClarificationAnswer}
          className="mt-4 rounded-2xl border-[1.5px] border-[var(--plum)] bg-white p-4 shadow-[4px_4px_0_var(--plum)]"
        >
          <div className="flex items-start gap-3">
            <Mag size={32} mood="confused" />
            <div className="flex-1">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
                {'↘'} <Trans>mag's asking</Trans>
              </span>
              <p className="mt-1 font-display text-[16px] font-bold leading-snug tracking-[-0.01em] text-[var(--ink)]">
                {pendingClarification.question}
              </p>
              <p className="mt-1 text-[12.5px] text-[var(--ink)]/65">
                <Trans>she won't make this up. give her the specifics and she'll draft it.</Trans>
              </p>
              <label className="field mt-3">
                <textarea
                  value={clarificationAnswer}
                  onChange={(e) => setClarificationAnswer(e.target.value)}
                  rows={3}
                  disabled={drafting.isPending}
                  maxLength={2000}
                />
              </label>
              <div className="mt-3 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setPendingClarification(null);
                    setClarificationAnswer('');
                  }}
                  disabled={drafting.isPending}
                  className="link-quiet text-[12px] disabled:opacity-50"
                >
                  <Trans>cancel</Trans>
                </button>
                <button
                  type="submit"
                  disabled={drafting.isPending || !clarificationAnswer.trim()}
                  className="btn disabled:opacity-60"
                >
                  {drafting.isPending ? (
                    <Trans>mag is drafting…</Trans>
                  ) : (
                    <Trans>send + draft</Trans>
                  )}
                  <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : null}

      <div className="mt-6">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
          {'↘'}{' '}
          {plural(assets.length, {
            one: '# draft for this campaign',
            other: '# drafts for this campaign',
          })}
        </p>
        {items.length === 0 ? (
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink)]/45">
            {'↘'} <Trans>no drafts yet · ask mag above to start</Trans>
          </p>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <GalleryTile
                key={item.kind === 'carousel' ? item.groupId : item.asset.id}
                item={item}
                onOpen={setOpenAssetId}
              />
            ))}
          </div>
        )}
      </div>

      {openAsset ? (
        <CreativeDetailDrawer
          asset={openAsset}
          {...(openCarouselSlides ? { groupSlides: openCarouselSlides } : {})}
          onClose={() => setOpenAssetId(null)}
        />
      ) : null}
    </section>
  );
}

// Built at render, not at module level: a module-level t`` evaluates
// before any locale is active and bakes English in for the process. The
// `value`s are api enums and stay as they are.
function campaignFormats(t: T): Array<{ value: CreativeAssetFormat; label: string }> {
  return [
    { value: 'feed_1x1', label: i18n._(msg`feed · 1:1`) },
    { value: 'feed_4x5', label: i18n._(msg`feed · 4:5`) },
    { value: 'story_9x16', label: i18n._(msg`story / reel · 9:16`) },
    { value: 'carousel_1x1', label: i18n._(msg`carousel · 1:1`) },
  ];
}

/** Compose the starting brief mag uses when drafting creative for a
 *  campaign. The campaign has already gone through intake / drafting,
 *  so mag has her own positioning + pitch — we hand that back to her
 *  as the starting point. Falls back through the available signals so
 *  every campaign kind gets a usable brief. */
function composeCampaignBrief(campaign: CampaignDetail): string {
  const parts: string[] = [];
  if (campaign.name) parts.push(`Campaign: ${campaign.name}.`);
  if (campaign.shortPitch) parts.push(campaign.shortPitch);
  if (parts.length === 0) {
    // No mag-authored pitch yet — fall back to the founder's brief
    // verbatim. Drafting from the raw paragraph still works; the
    // template-fill agent will distill it.
    return campaign.brief;
  }
  // Append the original brief as supporting context so mag can
  // reach for specifics the short pitch lost.
  if (campaign.brief && campaign.brief.length > 0) {
    parts.push(`(original brief: ${campaign.brief})`);
  }
  return parts.join(' ');
}

/** Short "n minutes / hours / days ago" string. Doesn't update live —
 *  the page reloads on nav anyway, and the gap between "12s" and "13s"
 *  doesn't matter for a campaign that just launched. */
/** Wave 2's helper does the first four branches. "just now" / "5m ago" /
 *  "3h ago" / "2d ago" were English assembled in a .ts helper — no JSX to
 *  flag and no Intl call to grep — so nothing in this repo could see
 *  them. Past a month it still falls back to a date. */
function formatRelative(date: Date): string {
  const days = (Date.now() - date.getTime()) / (24 * 60 * 60 * 1000);
  if (days < 30) return formatRelativeTimeCompact(date);
  return formatDate(date, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Status pill for the campaign header. Mirrors the campaigns-list pill
// (BRIEFING/DRAFTING/LIVE/etc.) so the founder reads continuity between
// the list row they tapped and this page. Only `launched` + `failed`
// reach this route — earlier statuses redirect in beforeLoad — but we
// keep the full map for completeness in case routing changes.
// Both maps were module-level consts. A module-level t`` evaluates
// before any locale is active and bakes English in for the process, so
// each is built during render instead. The KEYS are api enums and stay.
function headerStatusPills(
  t: T,
): Record<CampaignDetail['status'], { label: string; bg: string; fg: string }> {
  return {
    intake: { label: i18n._(msg`BRIEFING`), bg: 'var(--paper)', fg: 'var(--ink)' },
    drafting: { label: i18n._(msg`DRAFTING`), bg: 'var(--paper)', fg: 'var(--ink)' },
    ready: { label: i18n._(msg`READY`), bg: 'var(--plum)', fg: 'var(--cream)' },
    launched: { label: i18n._(msg`LIVE`), bg: 'var(--spell)', fg: 'var(--ink)' },
    failed: { label: i18n._(msg`FAILED`), bg: 'var(--pulse)', fg: 'var(--cream)' },
    // Reachable since campaign-level pause and the archiving delete started
    // writing them. 'archived' renders as DELETED because that is the word the
    // founder used when they pressed the button; the row survives for the
    // record, which is Magig's business and not theirs.
    paused: { label: i18n._(msg`PAUSED`), bg: 'var(--paper)', fg: 'var(--ink)' },
    archived: { label: i18n._(msg`DELETED`), bg: 'var(--paper)', fg: 'var(--ink)' },
  };
}

function headerKindLabels(t: T): Record<CampaignDetail['kind'], string | null> {
  return {
    analytics_setup: i18n._(msg`ANALYTICS`),
    creative_ads: i18n._(msg`ADS`),
    conversion_path: i18n._(msg`FUNNEL`),
    organic_meta: i18n._(msg`INSTAGRAM`),
    unclassified: null,
    // Imported paid campaigns are reachable through the takeover view
    // (/campaigns/imported/$id), not this detail page — but the map is
    // exhaustive on CampaignDetail['kind'] so the entry is required
    // for compile.
    imported_paid: i18n._(msg`ADS`),
  };
}

/** Paid ads on this campaign that are NOT running on their platform.
 *  A mock launch counts as unlaunched — it never reached google or
 *  meta, which is the same thing as far as the founder's money goes.
 *
 *  Deliberately does NOT filter on connected platforms, unlike
 *  launchablePaidAds: whether an ad is live is a fact about the ad, not
 *  about whether we currently have an account to launch it through. An
 *  ad on a disconnected platform is still not live, and the header must
 *  not call it LIVE. */
/** The paid deliverables we actually pushed to a platform — the only
 *  ones a platform reading can exist for. */
export function launchedPaidDeliverables(campaign: CampaignDetail): Array<{
  launched_at?: string | null | undefined;
  launched_via?: string | null | undefined;
  id?: string | undefined;
}> {
  if (!campaign.draft) return [];
  return draftToPlanView(campaign.draft).deliverables.filter(
    (d): d is typeof d & { launched_at?: string | undefined; launched_via?: string | undefined } =>
      (d.kind === 'meta_paid_ad' || d.kind === 'google_search_ad') &&
      'launched_at' in d &&
      Boolean(d.launched_at),
  );
}

/** What the founder is committing per day, summed across launched paid
 *  deliverables.
 *
 *  Read off the plan view rather than off budgetProposal, which is null
 *  on exactly the campaigns that most need the number — it refuses
 *  itself when the loss is ad rank. Returns null rather than 0 when
 *  nothing is launched, because "$0/day" is a claim and "we don't know"
 *  is the truth. */
function dailyBudgetUsd(campaign: CampaignDetail): number | null {
  if (!campaign.draft) return null;
  const total = draftToPlanView(campaign.draft)
    .deliverables.filter(
      (d) =>
        (d.kind === 'google_search_ad' || d.kind === 'meta_paid_ad') &&
        'launched_at' in d &&
        Boolean(d.launched_at),
    )
    .reduce((sum, d) => sum + (('daily_budget_usd' in d && d.daily_budget_usd) || 0), 0);
  return total > 0 ? total : null;
}

/** Mag's line under the pill when the platform isn't serving yet.
 *
 *  Says what is happening and what she is doing about it. Google's
 *  reason codes are SHOUTY_SNAKE_CASE; only the ones worth naming get a
 *  phrase, and anything else falls back to something true but general
 *  rather than leaking an enum value at a founder. */
function servingNoteFor(campaign: CampaignDetail): string {
  const readings = Object.values(campaign.platformServingStatus ?? {});
  const reasons = readings.flatMap((r) => r.reasons);
  const phrase = servingReasonPhrase(reasons);
  if (!phrase) {
    return "google hasn't started serving this yet. i'm watching it and i'll tell you the moment it does.";
  }
  // THE WAIT PROMISE BELONGS ONLY TO REVIEW. It used to be appended to every
  // phrase, so a BUDGET_CONSTRAINED campaign read "there's something off with
  // the budget. it usually takes a few hours — i'll tell you the moment it
  // starts serving." That is false twice over: a budget ceiling does not clear
  // itself in a few hours, and promising to announce a transition that will
  // never come tells the founder to sit still on the one state here they can
  // actually fix.
  //
  // Review genuinely does resolve on its own, and waiting IS the action there,
  // so that branch keeps its sentence.
  const clearsItself =
    reasons.includes('MOST_ADS_UNDER_REVIEW') ||
    reasons.includes('ADS_UNDER_REVIEW') ||
    reasons.includes('CAMPAIGN_PENDING');
  return clearsItself
    ? `${phrase}. it usually takes a few hours — i'll tell you the moment it starts serving.`
    : `${phrase}, and that won't clear on its own.`;
}

export function unlaunchedPaidAdCount(campaign: CampaignDetail): number {
  if (!campaign.draft) return 0;
  return draftToPlanView(campaign.draft).deliverables.filter(
    (d) =>
      (d.kind === 'meta_paid_ad' || d.kind === 'google_search_ad') &&
      (!('launched_at' in d && d.launched_at) ||
        ('launched_via' in d && d.launched_via === 'mock')),
  ).length;
}

/** The "subject card" at the top of the detail page. Before s154 the
 *  header was a plain eyebrow + display title, which looked identical
 *  to every subsection heading below it ("↘ ongoing reads / what's mag
 *  noticed lately") — the page read as one of N sections rather than
 *  "you're looking at this campaign." Wrapping the identity in a
 *  bordered card with the kind chip + status pill + short pitch lets
 *  the campaign claim the page; subsections inside InsightsPanel get a
 *  quieter heading treatment so the hierarchy reads cleanly. */
/** The identity bits the paid rail and the organic sidebar both need.
 *
 *  Extracted from CampaignHeader (deleted in spec 5614cfab slice 1, when
 *  the paid layout stopped having a full-width header card). The pill
 *  derivation is the valuable part and it is subtle — read the comments
 *  before changing the order of the branches.
 *
 *  THE PILL LABELS USE i18n._(msg`…`), NOT t`…`.
 *
 *  Inside CampaignHeader they were t`…` and that was correct: it was a
 *  component, so useLingui() was in scope for the macro to bind. Here `t`
 *  arrives as an argument, which the macro cannot see — the first cut of
 *  this extraction kept t`NOT LIVE YET` and silently DROPPED its existing
 *  Spanish ("AÚN NO EN VIVO") from the catalog. The code still compiled
 *  and still rendered correct English, which is why the catalog diff was
 *  the only thing that caught it. `t` is still a parameter because
 *  headerStatusPills / headerKindLabels genuinely take one. */
function campaignIdentity(
  campaign: CampaignDetail,
  t: T,
): {
  /** `string | null`, not `undefined` — headerKindLabels maps the
   *  `unclassified` kind to null deliberately. */
  kindLabel: string | null;
  pill: { label: string; bg: string; fg: string };
  servingNote: string | null;
  serving: ServingState;
} {
  // s156 — when Mag's been paused on this campaign, the status pill
  // flips from LIVE → PAUSED. The LIFECYCLE status (launched) is
  // unchanged on the row, but the pill carries the more important
  // *current* signal for the founder: is Mag working on this right now?
  const isPaused = campaign.scanCadence === 'off';

  // 'launched' is MAGIG's lifecycle state — the plan shipped and the
  // deliverables exist. It is NOT "running on google". A paid ad is
  // only live once it has a launched_at from a real (non-mock) push,
  // and the founder's approval does not do that; a separate launch
  // click does. The pill read the lifecycle status and printed LIVE, so
  // a campaign with an ad still sitting unlaunched announced itself as
  // live at the top of the page while the one remaining action was
  // below the fold. Say what is actually true.
  const pendingLaunches = unlaunchedPaidAdCount(campaign);
  // Spec da3515af — the step above says the ad is launched by US. That
  // still isn't "running on google": google reviews every new ad before
  // it serves, and a campaign can sit PENDING for hours while this page
  // says LIVE and spend stays $0. The founder only finds out by opening
  // google ads, which is the thing magig exists to spare them. So the
  // platform's own answer, when we have a fresh one, outranks ours.
  const serving = campaignServingState(campaign, launchedPaidDeliverables(campaign));
  const pill =
    isPaused && campaign.status === 'launched'
      ? { label: 'PAUSED', bg: 'var(--paper)', fg: 'var(--ink)' }
      : campaign.status === 'launched' && pendingLaunches > 0
        ? { label: i18n._(msg`NOT LIVE YET`), bg: 'var(--plum)', fg: 'var(--cream)' }
        : campaign.status === 'launched' && serving === 'pending'
          ? // Deliberately NOT an error colour. Review is the normal
            // path for every new ad and usually clears within a day —
            // making it alarming would train founders to panic at the
            // expected case, which is its own kind of lie.
            { label: i18n._(msg`IN REVIEW`), bg: 'var(--plum)', fg: 'var(--cream)' }
          : campaign.status === 'launched' && serving === 'limited'
            ? { label: i18n._(msg`LIMITED`), bg: 'var(--plum)', fg: 'var(--cream)' }
            : headerStatusPills(t)[campaign.status];
  const servingNote =
    campaign.status === 'launched' && (serving === 'pending' || serving === 'limited')
      ? servingNoteFor(campaign)
      : null;
  return { kindLabel: headerKindLabels(t)[campaign.kind], pill, servingNote, serving };
}

/** Kind + status chips, at rail scale. */
function IdentityChips({
  kindLabel,
  pill,
}: {
  kindLabel: string | null;
  pill: { label: string; bg: string; fg: string };
}) {
  return (
    <>
      {kindLabel ? (
        <span className="inline-flex items-center rounded-full border border-[var(--ink)]/15 bg-[var(--paper)] px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[var(--ink)]/75">
          {kindLabel}
        </span>
      ) : null}
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.1em]"
        style={{ background: pill.bg, color: pill.fg }}
      >
        {pill.label}
      </span>
    </>
  );
}

/** s233.10 — sidebar for the organic_meta detail layout. Carries
 *  identity (autonamed title + pitch + kind/status chips), the
 *  compact autopilot toggle, and at-a-glance plan stats (cadence,
 *  slots, last revision, next publish). Sticky on lg+ so the
 *  founder always reads "what kind of push am i looking at" while
 *  scrolling the slot list on the right.
 *
 *  Decoupled from CampaignHeader (which other kinds still use) so
 *  the visual hierarchy here can lean heavier on display type +
 *  sidebar-scale spacing without touching the paid card layout. */
function OrganicMetaSidebar({
  campaign,
  cmpId,
  shipped,
}: {
  campaign: CampaignDetail;
  cmpId: string;
  shipped: Date | null;
}) {
  const { t } = useLingui();
  const draft = campaign.draft;
  const plan =
    draft && 'cadence' in draft && 'slots' in draft
      ? (draft as Extract<typeof draft, { cadence: unknown; slots: unknown[] }>)
      : null;
  const kindLabel = headerKindLabels(t)[campaign.kind];
  const pill = headerStatusPills(t)[campaign.status];

  // Plan-derived stats. Cheap to compute on every render; the plan
  // shape isn't huge. Falls through to nulls/zeros when the plan
  // isn't ready yet (drafting state).
  const totalSlots = plan?.slots.length ?? 0;
  const publishedSlots = plan?.slots.filter((s) => s.draft?.publishedAt).length ?? 0;
  const draftedSlots = plan?.slots.filter((s) => s.draft).length ?? 0;
  const nextSlot = plan?.slots
    .filter((s) => !s.draft?.publishedAt && !s.draft?.skippedAt)
    .sort((a, b) => `${a.date}T${a.hour}`.localeCompare(`${b.date}T${b.hour}`))[0];

  return (
    <aside>
      <div className="flex flex-col gap-5 lg:sticky lg:top-20">
        {/* Identity card — eyebrow chips, the autonamed display title,
            and the short pitch as Mag-voice upright-serif quote
            (.mag-voice; italic dropped in s216-54h.36). Spell-shadow
            mirrors the brand's most-distinctive surface treatment. */}
        <div className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
          <div className="flex flex-wrap items-center gap-1.5">
            {kindLabel ? (
              <span className="inline-flex items-center rounded-full border border-[var(--ink)]/15 bg-[var(--paper)] px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[var(--ink)]/75">
                {kindLabel}
              </span>
            ) : null}
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.1em]"
              style={{ background: pill.bg, color: pill.fg }}
            >
              {pill.label}
            </span>
          </div>
          <EditableCampaignName campaign={campaign} />

          {campaign.shortPitch ? (
            <p className="mt-2 mag-voice text-[14px] leading-snug text-[var(--ink)]/70">
              “{campaign.shortPitch}”
            </p>
          ) : null}
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/45">
            {cmpId}
            {shipped ? ` · shipped ${formatRelative(shipped)}` : ''}
          </p>
        </div>

        {/* Autopilot lives in the sidebar so the toggle is always one
            scroll away while the founder is reviewing slots. Same
            component the paid layout uses; renders nothing when
            autopilot doesn't apply to this kind. */}
        <AutopilotToggle campaign={campaign} />

        {plan ? (
          <div className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>at a glance</Trans>
            </p>
            <dl className="mt-3 space-y-2.5 text-[12.5px]">
              <SidebarStat
                label={t`cadence`}
                value={`${plan.cadence.planDays}d · ${plan.cadence.postsPerWeek}/wk`}
              />
              <SidebarStat
                label={t`slots`}
                value={
                  publishedSlots > 0
                    ? `${publishedSlots}/${totalSlots} live · ${draftedSlots} drafted`
                    : `${draftedSlots}/${totalSlots} drafted`
                }
              />
              {nextSlot ? (
                <SidebarStat
                  label={t`next`}
                  value={`${nextSlot.format} · ${nextSlot.date} ${String(nextSlot.hour).padStart(2, '0')}:00`}
                />
              ) : null}
              {plan.pillars.length > 0 ? (
                <SidebarStat
                  label={t`pillars`}
                  value={`${plan.pillars.length} (${plan.pillars
                    .map((p) => p.label)
                    .slice(0, 3)
                    .join(', ')}${plan.pillars.length > 3 ? '…' : ''})`}
                />
              ) : null}
            </dl>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

/** s233.13 — click-to-edit campaign name. Plain title click opens
 *  an inline input pre-filled with the current name (or empty for
 *  unnamed campaigns). Enter or blur saves; Escape cancels. Empty
 *  submit clears the name (server falls back to kind default).
 *  Optimistic update via router.invalidate() after the PATCH lands. */
function EditableCampaignName({ campaign }: { campaign: CampaignDetail }) {
  const { t } = useLingui();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(campaign.name ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEdit = () => {
    setDraft(campaign.name ?? '');
    setEditing(true);
  };

  const cancel = () => {
    setDraft(campaign.name ?? '');
    setEditing(false);
  };

  const save = async () => {
    if (saving) return;
    const trimmed = draft.trim();
    const next = trimmed.length === 0 ? null : trimmed;
    // No change → just close.
    if (next === (campaign.name ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await renameCampaign(campaign.id, next);
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
      setEditing(false);
    } catch (err) {
      magToast.error("couldn't rename.", founderErrorCopy(err));
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={onKey}
        maxLength={80}
        disabled={saving}
        placeholder={t`name this campaign…`}
        aria-label={t`campaign name`}
        className="mt-3 w-full rounded-md border-[1.5px] border-[var(--ink)] bg-[var(--paper)] px-2 py-1 font-display text-[26px] font-black leading-[1.05] tracking-[-0.03em] text-[var(--ink)] outline-none focus:shadow-[3px_3px_0_var(--plum)] disabled:opacity-60"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      className="group mt-3 inline-flex items-baseline gap-2 text-left"
      title={t`click to rename`}
    >
      <h1 className="font-display text-[26px] font-black leading-[1.05] tracking-[-0.03em] text-[var(--ink)]">
        {campaign.name ?? t`organic instagram push`}
      </h1>
      <span
        aria-hidden="true"
        className="font-mono text-[10px] text-[var(--ink)]/0 transition-opacity group-hover:text-[var(--ink)]/45"
      >
        ✎
      </span>
    </button>
  );
}

/** Single-line label/value row for the sidebar "at a glance" stats.
 *  Mono label up top, ink value below — matches the typography of
 *  the settings rail subtitles so the two sidebars feel like kin. */
/** [spec ba7c4612] The budget stat, editable in place.
 *
 *  Wears SidebarStat's clothes when idle — the founder asked to see the
 *  budget here, and a row that shouts "form" would make the rail a
 *  control panel instead of a summary. The input only appears once they
 *  click the number.
 *
 *  WHAT IT RENDERS AFTER A SAVE IS THE SERVER'S actualTotalUsd, never
 *  the number typed. The campaign budget is a sum fanned across
 *  deliverables and a leg can fail against the platform, so the two
 *  genuinely differ sometimes — and the whole reason this path re-reads
 *  is so the page cannot claim a total Google never accepted. */
/** [spec ba7c4612] Days per month for the daily↔monthly lens.
 *
 *  30, not 30.4, and not because 30 is rounder. mag-strategist.ts
 *  documents monthlyBudgetUsd as "Sum of channels[].dailyBudgetUsd ×
 *  30" and a validator enforces it, so mag's own proposals already
 *  speak in thirtieths. A second convention here would have the
 *  campaign page quietly disagree with the proposal that created the
 *  campaign it is describing. */
const DAYS_PER_MONTH = 30;

function BudgetStat({ campaign, dailyBudget }: { campaign: CampaignDetail; dailyBudget: number }) {
  const { t } = useLingui();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(String(dailyBudget));
  // The monthly field's own text while the founder types in it. Null
  // means "derive me from daily", which is every moment except the one
  // where they are editing the monthly directly.
  //
  // Separate state rather than deriving both ways from one number,
  // because the round-trip is lossy: $100/mo is $3/day is $90/mo, and a
  // field that rewrote itself to 90 under the cursor would be fighting
  // the person typing into it.
  const [monthlyDraft, setMonthlyDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The unfinished state, kept in the ROW rather than a toast.
  //
  // A toast was wrong twice over. It vanishes while the condition does
  // not — the campaign really is at $14 when $20 was asked for, and
  // that stays true until someone acts. And it was an ERROR toast for
  // something that is not an error: most of what the founder asked for
  // happened. This renders under the number, persistently, until a
  // later save resolves it.
  const [partial, setPartial] = useState<{
    asked: number;
    actual: number;
    failure: CampaignBudgetResult['failure'];
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // FOCUS ON THE FOUNDER'S ACTION, not on mount, and without scrolling.
  // autoFocus would be wrong twice over here: the shared-input rule asks
  // surfaces to opt in deliberately, and WCAG 2.4.3 is about focus moving
  // on load. This input only exists after a click on the value it
  // replaces — so focusing it IS following the click — but it sits in a
  // sidebar partway down a long page, and a plain focus() would scroll
  // the campaign out from under the founder to bring it into view.
  // preventScroll keeps the page where they left it.
  useEffect(() => {
    if (editing) inputRef.current?.focus({ preventScroll: true });
  }, [editing]);

  // Takes the amount explicitly so "finish it" can re-send the total
  // the founder originally asked for without routing through the input.
  // What the daily field currently says, as a number. Drives the
  // monthly box when the founder is not typing in it, and the "what
  // this actually comes to" line below the pair.
  const parsedDaily = Number.parseInt(draftValue, 10);
  const dailyIsValid = Number.isFinite(parsedDaily) && parsedDaily >= 1 && parsedDaily <= 10000;

  const save = async (amount?: number) => {
    const next = amount ?? Number.parseInt(draftValue, 10);
    if (!Number.isFinite(next) || next < 1 || next > 10000) {
      magToast.error(t`that budget doesn't look right.`, t`whole dollars, between $1 and $10000.`);
      return;
    }
    if (next === dailyBudget) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setPartial(null);
    try {
      const result = await setCampaignBudget(campaign.id, next);
      setEditing(false);
      if (result.complete) {
        setPartial(null);
        magToast.success(
          t`budget is $${result.actualTotalUsd}/day.`,
          t`live on the platform — it takes a few hours to settle on the new envelope.`,
        );
      } else {
        // No toast. The row says it, and keeps saying it — see the
        // comment on `partial`. Transient platform errors never get
        // here; the server already retried those twice.
        setPartial({
          asked: result.requestedTotalUsd,
          actual: result.actualTotalUsd,
          failure: result.failure,
        });
      }
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error(t`couldn't change the budget.`, founderErrorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div>
        <dt className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/45">
          <Trans>budget</Trans>
        </dt>
        <dd className="mt-0.5 flex items-baseline gap-2">
          <button
            type="button"
            onClick={() => {
              setDraftValue(String(dailyBudget));
              setMonthlyDraft(null);
              setEditing(true);
            }}
            className="font-display text-[13px] font-semibold leading-snug text-[var(--ink)]/85 underline decoration-[var(--ink)]/20 decoration-dashed underline-offset-4 transition-colors hover:text-[var(--ink)] hover:decoration-[var(--plum)]"
          >
            <Trans>${dailyBudget}/day</Trans>
          </button>
          {/* The founder thinks in months; the platform bills by day.
              Shown as an estimate, with the ≈, because a 30-day month
              is a convention rather than a calendar. */}
          <span className="font-mono text-[10px] text-[var(--ink)]/45">
            <Trans>≈ ${dailyBudget * DAYS_PER_MONTH}/mo</Trans>
          </span>
          {partial ? (
            <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
              <Trans>· ${partial.asked} asked</Trans>
            </span>
          ) : null}
        </dd>
        {partial ? (
          // THE ACTION MATCHES THE CAUSE. A missing scope will fail the
          // same way on every retry until the connection is re-granted,
          // so offering "finish it" there would be a button that cannot
          // work. Meta has a self-serve re-grant; google does not, so
          // that case gets the reason and no false promise of a fix.
          <dd className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="mag-voice text-[12px] leading-[1.4] text-[var(--ink)]/70">
              {partial.failure?.kind === 'scope' ? (
                <Trans>“one ad wouldn't take it — that connection needs more access.”</Trans>
              ) : (
                <Trans>“one ad wouldn't take it. the rest moved.”</Trans>
              )}
            </span>
            {partial.failure?.kind === 'scope' && partial.failure.extendUrl ? (
              <a href={partial.failure.extendUrl} className="btn btn-sm">
                <Trans>extend connection</Trans>
                <span className="btn-arrow">→</span>
              </a>
            ) : partial.failure?.kind === 'scope' ? null : (
              <button
                type="button"
                onClick={() => {
                  setDraftValue(String(partial.asked));
                  void save(partial.asked);
                }}
                disabled={busy}
                className="rounded-md border border-[var(--ink)]/25 px-2 py-1 font-mono text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink)]/70 transition hover:border-[var(--ink)] hover:text-[var(--ink)] disabled:opacity-50"
              >
                {busy ? <Trans>finishing…</Trans> : <Trans>finish it</Trans>}
              </button>
            )}
          </dd>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <dt className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/45">
        <Trans>budget</Trans>
      </dt>
      <dd className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="font-display text-[13px] text-[var(--ink)]/60">$</span>
        <input
          ref={inputRef}
          type="number"
          min={1}
          max={10000}
          step={1}
          inputMode="numeric"
          value={draftValue}
          disabled={busy}
          aria-label={t`daily budget in dollars`}
          onChange={(e) => {
            setDraftValue(e.target.value);
            // Typing the daily hands the monthly back to derivation.
            setMonthlyDraft(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-[72px] rounded-md border border-[var(--ink)]/25 bg-white px-2 py-1 font-display text-[13px] font-semibold text-[var(--ink)] focus:border-[var(--plum)] focus:outline-none disabled:opacity-50"
        />
        <span className="font-mono text-[10px] text-[var(--ink)]/45">
          <Trans>/day</Trans>
        </span>
        {/* THE MONTHLY IS A LENS, NOT A SECOND FIELD OF RECORD. Typing
            here back-calculates a daily, which is what actually gets
            sent — the platform has no concept of a monthly budget.
            Whichever box has the cursor keeps its own text; the other
            derives. */}
        <span className="font-mono text-[10px] text-[var(--ink)]/35">·</span>
        <span className="font-display text-[13px] text-[var(--ink)]/60">$</span>
        <input
          type="number"
          min={DAYS_PER_MONTH}
          max={10000 * DAYS_PER_MONTH}
          step={DAYS_PER_MONTH}
          inputMode="numeric"
          value={monthlyDraft ?? String(parsedDaily * DAYS_PER_MONTH)}
          disabled={busy}
          aria-label={t`monthly budget in dollars`}
          onChange={(e) => {
            const raw = e.target.value;
            setMonthlyDraft(raw);
            const monthly = Number.parseInt(raw, 10);
            if (Number.isFinite(monthly)) {
              // Floor, not round: a founder who says $100/mo should
              // not be handed $3.33/day rounded UP to $4, which bills
              // them $120. Under their number is the safe direction.
              const daily = Math.max(1, Math.floor(monthly / DAYS_PER_MONTH));
              setDraftValue(String(daily));
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-[86px] rounded-md border border-[var(--ink)]/25 bg-white px-2 py-1 font-display text-[13px] font-semibold text-[var(--ink)] focus:border-[var(--plum)] focus:outline-none disabled:opacity-50"
        />
        <span className="font-mono text-[10px] text-[var(--ink)]/45">
          <Trans>/mo</Trans>
        </span>
      </dd>
      {/* ACTIONS ON THEIR OWN ROW, right-aligned, cancel left of save.
          Two number inputs plus two buttons do not fit across a 230px
          sidebar, so flex-wrap broke the row and left SAVE orphaned at
          the left margin under the fields — which rule c08c7d48 forbids
          twice over: it was left-aligned, and the cancel sat to the
          RIGHT of the primary. The founder read it as broken, which it
          was. */}
      <dd className="mt-2 flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={busy}
          className="rounded-md px-2 py-1 font-mono text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink)]/45 transition hover:text-[var(--ink)] disabled:opacity-50"
        >
          <Trans>cancel</Trans>
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          className="rounded-md border border-[var(--ink)]/25 px-2.5 py-1 font-mono text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink)]/70 transition hover:border-[var(--ink)] hover:text-[var(--ink)] disabled:opacity-50"
        >
          {busy ? <Trans>saving…</Trans> : <Trans>save</Trans>}
        </button>
      </dd>
      {/* A monthly that is not a multiple of 30 cannot be honoured
          exactly — $100/mo is $3/day is $90/mo — and echoing the typed
          number back would be the page agreeing to something the
          platform will not do. Say what it actually comes to, and only
          when the two disagree. */}
      {dailyIsValid &&
      monthlyDraft !== null &&
      Number.parseInt(monthlyDraft, 10) !== parsedDaily * DAYS_PER_MONTH ? (
        <dd className="mt-1 font-mono text-[10px] leading-[1.4] text-[var(--ink)]/50">
          {'↘'}{' '}
          <Trans>
            that lands on ${parsedDaily}/day — about ${parsedDaily * DAYS_PER_MONTH}/mo, since the
            platform bills daily.
          </Trans>
        </dd>
      ) : null}
    </div>
  );
}

function SidebarStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/45">
        {label}
      </dt>
      <dd className="mt-0.5 font-display text-[13px] font-semibold leading-snug text-[var(--ink)]/85">
        {value}
      </dd>
    </div>
  );
}

// s233.44 — InstagramPostPreview + IgGlyph were inlined here in
// s233.19; moved them to apps/dashboard/src/components/instagram-post
// -preview.tsx so the home page's drop concepts can render in the
// same chrome (per founder feedback that home posts looked nothing
// like IG posts). Same component, same behavior — import above.

/** s157 — Mag-voice "i'll check back in X" line shown on the insights
 *  panel. First-person serif (Mag is literally narrating), upright at
 *  15px so a single-line standalone reads cleanly without leaning on
 *  italic for the voice cue. Used in two contexts:
 *
 *    - empty state (no insights yet): "i'll read this on my own
 *      tomorrow." — tells the founder Mag will eventually scan even if
 *      they never press the button.
 *    - has-latest insight: "i'll check back in 12h." — sits alongside
 *      the "ask again" button so the row balances Mag's plan with the
 *      manual escape hatch. */
function NextReadLine({ nextScanAt, hasFirstRead }: { nextScanAt: string; hasFirstRead: boolean }) {
  const { t } = useLingui();
  const voice = formatNextReadVoice(nextScanAt, t);
  const body = hasFirstRead ? t`i'll check back ${voice}.` : t`i'll read this on my own ${voice}.`;
  return (
    <p className="mt-5 text-center font-serif text-[15px] leading-snug text-[var(--ink)]/70">
      <Trans>“{body}”</Trans>
    </p>
  );
}

/** Render an ISO timestamp as a Mag-voice future-time phrase. Uses
 *  natural English ("in about 3 hours", "tomorrow", "in 2 days") rather
 *  than the compact "in 3h" the home row uses — the campaign detail
 *  page has the breathing room for the warmer phrasing. */
function formatNextReadVoice(iso: string, t: T): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 60_000) return i18n._(msg`any minute now`);
  const m = Math.floor(ms / 60_000);
  if (m < 60) return plural(m, { one: 'in about # minute', other: 'in about # minutes' });
  const h = Math.floor(m / 60);
  if (h === 1) return i18n._(msg`in about an hour`);
  if (h < 24) return plural(h, { other: 'in about # hours' });
  const d = Math.floor(h / 24);
  if (d === 1) return i18n._(msg`tomorrow`);
  if (d < 7) return plural(d, { other: 'in # days' });
  return i18n._(msg`next week`);
}

/** Tombstone for non-analytics_setup campaigns — they don't have a
 *  goal/event-set anchor for proactive scans today, so the details
 *  page just acknowledges that and points the founder back to the
 *  list. Future kinds can drop their own surface in here. */
/** Pause / resume for the whole campaign, in the rail.
 *
 *  Delete is NOT here. It lives in a DangerZone at the foot of the main
 *  column, which is where this codebase already puts destructive actions
 *  (settings/sections/danger.tsx). It was briefly a plain outlined button
 *  stacked under resume, which made deleting a campaign look exactly like
 *  pausing one — the founder said so, and they were right.
 *
 *  Pause stays in the rail on purpose: it is reversible and ordinary, and
 *  putting it inside a danger zone would say the wrong thing about it. */
function CampaignPauseBlock({ campaign }: { campaign: CampaignDetail }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  const paused = campaign.status === 'paused';
  // Same gate the list row uses: offered only where there is a real platform
  // launch to act on, which is exactly the set the api would otherwise refuse
  // with 'nothing_running'.
  const canPause = campaign.paidDelivery !== null;
  if (!canPause) return null;

  const togglePause = async () => {
    setBusy(true);
    try {
      if (paused) {
        const out = await resumeCampaign(campaign.id);
        if (out.stillPaused.length > 0) {
          magToast.error(
            t`resumed ${out.resumedCount}, but not all of them.`,
            t`${out.stillPaused.length} ad(s) wouldn't restart, so this one stays paused.`,
          );
        } else {
          magToast.success(t`back on.`, t`the ads are running and i'm watching again.`);
        }
      } else {
        const out = await pauseCampaign(campaign.id);
        if (out.campaignPaused) {
          magToast.success(t`paused.`, t`the ads are stopped and i've stepped back from this one.`);
        } else {
          magToast.error(
            t`paused ${out.pausedCount}, but not all of them.`,
            t`${out.stillRunning.length} ad(s) are still running, so this one is still live and i'm still watching.`,
          );
        }
      }
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't do that.", founderErrorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/45">
        {'\u2198'} <Trans>this campaign</Trans>
      </p>
      <button
        type="button"
        onClick={() => void togglePause()}
        disabled={busy}
        className="mt-3 w-full rounded-md border border-[var(--ink)]/25 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? (
          paused ? (
            <Trans>resuming…</Trans>
          ) : (
            <Trans>pausing…</Trans>
          )
        ) : paused ? (
          <Trans>resume this campaign</Trans>
        ) : (
          <Trans>pause this campaign</Trans>
        )}
      </button>
    </div>
  );
}

/** Campaign delete, in the shell every destructive action uses.
 *
 *  The copy names what actually happens, which for a campaign is unusual
 *  enough to spell out: the ads are REMOVED on google and meta, not paused,
 *  and the record is deliberately kept (decision 261f69c). A founder reading
 *  "this cannot be undone" should know which half that applies to. */
function CampaignDangerZone({ campaign, slug }: { campaign: CampaignDetail; slug: string }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const campaignLabel = campaign.name ?? campaign.brief.slice(0, 60);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteCampaign(campaign.id);
      magToast.success(t`campaign deleted.`, t`the ads are stopped and it's out of the list.`);
      void navigate({ to: '/brand/$slug/campaigns', params: { slug }, replace: true });
    } catch (err) {
      magToast.error(
        t`mag couldn't stop those ads — nothing was deleted.`,
        founderErrorCopy(err, 'the campaign is still running. try again in a moment.'),
      );
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <>
      <DangerZone
        // The main column is a gap-6 flex column and owns the rhythm; the
        // card's own mt-10 on top of that reads as a broken double gap.
        ownsSpacing={false}
        title={<Trans>delete this campaign</Trans>}
        action={
          <button type="button" onClick={() => setConfirmingDelete(true)} className="btn btn-pulse">
            <Trans>delete campaign</Trans>
            <span className="btn-arrow">→</span>
          </button>
        }
      >
        <Trans>
          removes <b>{campaignLabel}</b> on google and meta — the ads are deleted, not paused, and
          can't be put back. mag keeps the record so you can still see what ran and what it cost.{' '}
          <span className="font-bold">this cannot be undone.</span>
        </Trans>
      </DangerZone>
      <ConfirmDialog
        open={confirmingDelete}
        title={t`delete this campaign?`}
        body={
          <Trans>
            this stops <strong>{campaignLabel}</strong> on google and meta for good — the ads are
            removed, not paused, and can't be put back. mag keeps the record so you can still see
            what ran and what it cost.
          </Trans>
        }
        confirmLabel={t`stop the ads + delete`}
        destructive
        busy={deleting}
        busyLabel={t`deleting…`}
        onConfirm={confirmDelete}
        onCancel={() => (deleting ? undefined : setConfirmingDelete(false))}
      />
    </>
  );
}

/** What the page can still say when mag has no stored plan for a campaign.
 *
 *  Replaces OtherKindTombstone for this case. The tombstone's sentence is
 *  true of a campaign kind mag does not scan yet; it is false, and
 *  misleading, about a launched ads campaign whose draft is missing — and
 *  the founder cannot tell those apart from the copy.
 *
 *  Everything here comes from campaign COLUMNS and the verdict window, not
 *  from the draft, which is the whole point: these are the facts that
 *  survive a missing plan. The ad copy itself genuinely cannot be shown,
 *  because the plan is the only place Magig stores it — so the panel says
 *  that plainly and sends the founder to the platform rather than implying
 *  there is nothing to see. */
function MissingPlanPanel({
  campaign,
  shippedLabel,
  window: verdictWindow,
}: {
  campaign: CampaignDetail;
  shippedLabel: string | null;
  window: { totalSpendUsd: number; totalClicks: number } | null;
}) {
  const launched = Boolean(campaign.launchedAt);
  return (
    <div className="rounded-2xl border border-dashed border-[var(--ink)]/25 bg-[var(--paper)] p-8">
      <Mag size={48} mood="smug" />
      <p className="mt-3 font-serif text-[18px] leading-tight text-[var(--ink)]/85">
        {launched ? (
          <Trans>“this one shipped, but i don't have the plan for it saved.”</Trans>
        ) : (
          <Trans>“there's no plan saved for this one yet.”</Trans>
        )}
      </p>
      <p className="mt-2 mag-voice text-[14px] leading-snug text-[var(--ink)]/70">
        {launched ? (
          <Trans>
            the ad copy lives in the plan, so i can't show you what's running from here. what i do
            know is below — and the ad itself is on the platform.
          </Trans>
        ) : (
          <Trans>
            nothing has been drafted or launched on this one, so there's nothing for me to read yet.
          </Trans>
        )}
      </p>
      {launched ? (
        <dl className="mt-5 flex flex-wrap gap-x-10 gap-y-3 border-t border-[var(--line)] pt-4">
          {shippedLabel ? (
            <MissingPlanStat label={<Trans>shipped</Trans>} value={shippedLabel} />
          ) : null}
          {verdictWindow ? (
            <MissingPlanStat
              label={<Trans>spent</Trans>}
              value={verdictWindow.totalSpendUsd.toFixed(2)}
            />
          ) : null}
          {verdictWindow ? (
            <MissingPlanStat
              label={<Trans>clicks</Trans>}
              value={formatNumber(verdictWindow.totalClicks, { maximumFractionDigits: 0 })}
            />
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}

function MissingPlanStat({ label, value }: { label: ReactNode; value: string }) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/45">
        {label}
      </dt>
      <dd className="mt-1 font-serif text-[16px] text-[var(--ink)]">{value}</dd>
    </div>
  );
}

function OtherKindTombstone() {
  return (
    <div className="mx-auto mt-10 max-w-[680px] rounded-2xl border border-dashed border-[var(--ink)]/25 bg-[var(--paper)] p-8 text-center">
      <Mag size={56} mood="smug" />
      <p className="mt-3 font-serif text-[18px] leading-tight text-[var(--ink)]/85">
        <Trans>“this one's saved — no ongoing reads to show yet for this kind.”</Trans>
      </p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/45">
        {'↘'} <Trans>proactive insights live only on analytics_setup campaigns for now</Trans>
      </p>
    </div>
  );
}

/** Ongoing proactive-insights panel. Was originally the "below the
 *  hero" panel on the launched route; lives here on the details route
 *  in s135. Same logic, slightly tighter framing for the details
 *  context (no "what i'll be watching" hero/celebration framing). */
function InsightsPanel({ campaign }: { campaign: CampaignDetail }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // s156 — pause/resume submission. Separate from `submitting` so the
  // scan button doesn't grey out while the founder is toggling pause.
  const [togglingPause, setTogglingPause] = useState(false);
  const isPaused = campaign.scanCadence === 'off';

  const { goal, fallbackGoal } = useMemo(() => {
    const draft = campaign.draft;
    if (!draft) return { goal: null, fallbackGoal: null };
    // s182 — works on both legacy AnalyticsSetupDraft and the new
    // CampaignPlan shape. Plans expose `goal` directly; the
    // fallback is the plan-level postInstallCheck (when present),
    // same role as the legacy field.
    if ('shape' in draft && draft.shape === 'plan') {
      return {
        goal: draft.goal ?? null,
        fallbackGoal: draft.postInstallCheck ?? null,
      };
    }
    if ('kind' in draft && draft.kind === 'analytics_setup') {
      return {
        goal: draft.goal ?? null,
        fallbackGoal: draft.postInstallCheck ?? null,
      };
    }
    return { goal: null, fallbackGoal: null };
  }, [campaign.draft]);

  const insights = campaign.recentInsights ?? [];
  const latest = insights[0];
  const older = insights.slice(1);
  // s159.8 — chat lives in a drawer. Hold the open insight by id so we
  // re-render the drawer against the freshest server-side thread after
  // each send (insights array gets a new reference on every refetch).
  const [openInsightId, setOpenInsightId] = useState<string | null>(null);
  const openInsight = openInsightId ? (insights.find((i) => i.id === openInsightId) ?? null) : null;

  const onScan = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await scanCampaign(campaign.id);
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await router.invalidate();
    } catch (err) {
      const message = founderErrorCopy(err, 'something went wrong.');
      setErrorMessage(message);
      magToast.error(t`mag couldn't read.`, message);
    } finally {
      setSubmitting(false);
    }
  };

  /** s156 + s170 — flip scan_cadence between 'off' (pause) and 'auto'
   *  (resume). On resume the api primes next_scan_at = now so the
   *  founder sees Mag's first read on the next scheduler tick rather
   *  than 24h out. No confirm dialog — pause is fully reversible, the
   *  toast carries the "undo this any time" message. */
  const onTogglePause = async () => {
    setTogglingPause(true);
    try {
      await setCampaignScanCadence(campaign.id, isPaused ? 'auto' : 'off');
      await refreshQueries(queryClient, ['campaign', campaign.id]);
      await refreshQueries(queryClient, ['campaigns']);
      await router.invalidate();
      if (isPaused) {
        magToast.success(t`mag's back on it.`, t`her first read is queued.`);
      } else {
        magToast.info(t`paused.`, t`she won't read or suggest until you resume.`);
      }
    } catch (err) {
      magToast.error(
        isPaused ? "couldn't resume." : "couldn't pause.",
        founderErrorCopy(err, 'try again.'),
      );
    } finally {
      setTogglingPause(false);
    }
  };

  const isEmpty = !latest;
  const lastReadAgo = insights[0] ? formatRelative(new Date(insights[0].generatedAt)) : null;
  const nextReadVoice = campaign.nextScanAt ? formatNextReadVoice(campaign.nextScanAt, t) : null;
  const magSpend = formatCostUsd(campaign.actualCostUsd ?? 0);

  return (
    <div className="mx-auto mt-8 max-w-[820px]">
      {/* Goal sits at the very top — it's the north star every read below
          gets scored against, so it should be the first thing the eye
          lands on inside this panel. Heading + intro paragraph follow. */}
      <GoalBlock campaignId={campaign.id} goal={goal} fallbackGoal={fallbackGoal} />

      {/* s163 + s164 — open followups + recently closed history. Panel
          hides entirely when both lists are empty. */}
      {(campaign.openFollowups?.length ?? 0) > 0 ||
      (campaign.recentClosedFollowups?.length ?? 0) > 0 ? (
        <FollowupsPanel
          open={campaign.openFollowups ?? []}
          closed={campaign.recentClosedFollowups ?? []}
          campaignId={campaign.id}
          campaignName={campaign.name}
        />
      ) : null}

      {/* s154 — section heading inside the campaign subject card. We
          intentionally downshift this from h1-style weight so it doesn't
          read as a peer to the campaign's own title above. */}
      <p className="mt-8 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} {isPaused ? t`paused` : t`ongoing reads`}
      </p>
      <h2 className="mt-1.5 font-display text-[20px] sm:text-[22px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--ink)]/90">
        {isPaused
          ? t`mag isn't reading this right now.`
          : isEmpty
            ? t`what i'll be watching`
            : t`what's mag noticed lately`}
      </h2>
      <p className="mt-2 max-w-[620px] text-[14px] leading-[1.55] text-[var(--ink)]/70">
        {isPaused
          ? t`no automatic reads, no nudges, no suggested changes. resume any time — her first read after that lands within the hour.`
          : t`every read below scores this campaign against the goal above — what moved in the last 7 days and the one concrete change i’d make. tap any read to chat with me about it.`}
      </p>

      {isPaused ? (
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3 rounded-2xl border border-dashed border-[var(--ink)]/30 bg-[var(--paper)] px-5 py-4">
          <p className="mr-auto font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
            ↘ {lastReadAgo ? t`last read · ${lastReadAgo}` : t`no reads on file`}
          </p>
          <button
            type="button"
            onClick={onTogglePause}
            disabled={togglingPause}
            className="btn disabled:opacity-60"
          >
            {togglingPause ? t`resuming…` : t`resume mag`}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      ) : null}

      {/* s156 — when paused we skip the scan / ask-again UI entirely
          (Mag's whole point is she's not reading right now). History
          still renders below so the founder can re-read what landed
          before they paused. */}
      {!isPaused && isEmpty ? (
        <>
          <div className="mt-8 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-8 text-center shadow-[4px_4px_0_var(--plum)]">
            <Mag size={64} mood="smug" />
            <p className="mt-3 font-serif text-[20px] leading-tight text-[var(--ink)]">
              <Trans>“no reads yet. press the button for your first one.”</Trans>
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
              {'↘'} <Trans>~1 llm call · ~5–15 seconds</Trans>
            </p>
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={onScan}
                disabled={submitting}
                className="btn disabled:opacity-60"
                aria-label={t`ask mag what has changed`}
              >
                {submitting ? t`mag is reading…` : t`ask mag what's changed`}
                <span className="btn-arrow">→</span>
              </button>
            </div>
            {errorMessage ? (
              <p className="mt-3 inline-flex rounded-md border border-[var(--pulse)]/40 bg-[var(--pulse)]/5 px-3 py-2 text-[12px] leading-[1.45] text-[var(--pulse)]">
                ↘ {errorMessage}
              </p>
            ) : null}
          </div>
          {campaign.nextScanAt ? (
            <NextReadLine nextScanAt={campaign.nextScanAt} hasFirstRead={false} />
          ) : null}
        </>
      ) : null}

      {!isPaused && !isEmpty ? (
        <div className="mt-8">
          <InsightCard
            insight={latest}
            baseline={campaign.baselineInsight ?? null}
            variant="latest"
            onOpenChat={() => setOpenInsightId(latest.id)}
          />
          {/* Bottom row: mag-voice next-read line on the left,
              "ask mag about this" / "N messages" button on the right.
              The button label flips to a count once the thread has
              turns, so the founder can see at a glance which reads
              they've already pushed back on. */}
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            {/* s160 — mag now writes her own next-read reason on every
                scan. When present, render it verbatim (it's already a
                full sentence with the "why"). Fall back to the s157
                interval-only line for campaigns scanned before s160. */}
            {campaign.nextScanReason ? (
              <p className="mr-auto font-serif text-[15px] leading-snug text-[var(--ink)]/70">
                “{campaign.nextScanReason}”
              </p>
            ) : campaign.nextScanAt ? (
              <p className="mr-auto font-serif text-[15px] leading-snug text-[var(--ink)]/70">
                <Trans>“i'll check back {nextReadVoice}.”</Trans>
              </p>
            ) : null}
            {errorMessage ? (
              <p className="rounded-md border border-[var(--pulse)]/40 bg-[var(--pulse)]/5 px-3 py-1.5 text-[12px] leading-[1.45] text-[var(--pulse)]">
                ↘ {errorMessage}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => setOpenInsightId(latest.id)}
              className="btn btn-ghost text-[11px]"
            >
              {chatButtonLabel(latest.thread.length, t)}
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      ) : null}

      {older.length > 0 ? (
        <details className="mt-6">
          <summary className="cursor-pointer select-none font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink)]/60 hover:text-[var(--ink)]">
            {'↘'} <Trans>previous reads ({older.length})</Trans>
          </summary>
          <div className="mt-3 flex flex-col gap-3">
            {older.map((i) => (
              <InsightCard
                key={i.id}
                insight={i}
                baseline={campaign.baselineInsight ?? null}
                variant="older"
                onOpenChat={() => setOpenInsightId(i.id)}
              />
            ))}
          </div>
        </details>
      ) : null}

      {openInsight ? (
        <InsightChatDrawer
          headline={openInsight.headline}
          dateLabel={formatDateTime(openInsight.generatedAt, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
          thread={openInsight.thread}
          onSend={async (question, onEvent) => {
            // s175 — streaming variant. The endpoint pushes
            // tool_call_started / finished / done events; we forward
            // to the drawer's status line, then refresh once the
            // insight payload arrives at the end of the stream.
            await chatOnInsightStreaming(
              openInsight.campaignId,
              openInsight.id,
              question,
              (event) => {
                if (event.kind === 'insight' || event.kind === 'card' || event.kind === 'error')
                  return;
                onEvent?.(event);
              },
            );
            await refreshQueries(queryClient, ['campaign', openInsight.campaignId]);
            await router.invalidate();
          }}
          onClose={() => setOpenInsightId(null)}
        />
      ) : null}

      {/* s156 + s171 — bottom action row. Pause control on the right
          (quiet, settings-shaped); mag's actual spend on this campaign
          on the left so the founder sees the cost without leaving the
          page. Hidden when paused — the prominent "resume mag" CTA
          above is the right affordance in that state. */}
      {!isPaused ? (
        <div className="mt-10 flex items-center justify-between gap-3">
          <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
            {'↘'} <Trans>mag's spend on this campaign · {magSpend}</Trans>
          </p>
          <button
            type="button"
            onClick={onTogglePause}
            disabled={togglingPause}
            className="link-quiet text-[11px] disabled:opacity-60"
            aria-label={t`pause mag's reads on this campaign`}
          >
            {'↘'} {togglingPause ? t`pausing…` : t`pause mag's reads`}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Money formatter for mag's actual spend. Pennies need 3-4dp to be
 *  legible ($0.0023, not $0.00); dollars round to 2dp. */
function formatCostUsd(value: number): string {
  if (value <= 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

function InsightCard({
  insight,
  baseline,
  variant,
  onOpenChat,
}: {
  insight: CampaignInsight;
  baseline: CampaignInsight | null;
  variant: 'latest' | 'older';
  /** Open the chat drawer for this insight. The latest variant doesn't
   *  render its own affordance (the action row outside owns the
   *  prominent button); older variants render a count link below the
   *  card body. */
  onOpenChat: () => void;
}) {
  const { t } = useLingui();
  // Named before the message sees them: an expression inside <Trans>
  // extracts as {0}, and a translator reordering an unnamed placeholder
  // is guessing at what it holds.
  const anomalyLabel = insight.anomalyHint ? anomalyEyebrowLabel(insight.anomalyHint, t) : '';
  const recommendation = insight.recommendation;
  const generated = new Date(insight.generatedAt);
  const dateLabel = formatDateTime(generated, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const diffs =
    baseline && baseline.id !== insight.id && baseline.windowSnapshot && insight.windowSnapshot
      ? computeEventDiffs(insight.windowSnapshot, baseline.windowSnapshot)
      : null;

  if (variant === 'latest') {
    return (
      <article className="flex items-start gap-3">
        <Mag size={44} mood="smug" />
        <div className="flex-1">
          <p className="text-[12px] font-bold leading-tight">
            mag <span className="font-normal text-[var(--ink)]/50">· {dateLabel}</span>
          </p>
          <div className="mt-2 rounded-[16px_16px_16px_4px] border-[1.5px] border-[var(--ink)] bg-[var(--ink)] p-5 text-[var(--cream)] shadow-[4px_4px_0_var(--plum)]">
            {insight.anomalyHint ? (
              <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--spell)]">
                {'↘'} <Trans>anomaly · {anomalyLabel}</Trans>
              </p>
            ) : null}
            <p className="font-serif text-[20px] leading-tight text-[var(--spell)]">
              “{insight.headline}”
            </p>
            <p className="mt-3 text-[14.5px] leading-[1.55] opacity-90">{insight.body}</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--spell)]/80">
              {'↘'} <Trans>what i'd do</Trans>
            </p>
            <p className="mt-1.5 text-[14.5px] leading-[1.5] text-[var(--cream)]">
              {insight.recommendation}
            </p>
            {diffs && diffs.length > 0 ? (
              <>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--spell)]/80">
                  {'↘'} <Trans>since launch</Trans>
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {diffs.map((d) => (
                    <DiffChip key={d.name} diff={d} tone="dark" />
                  ))}
                </div>
              </>
            ) : null}
            {insight.toolCalls && insight.toolCalls.length > 0 ? (
              <InvestigationFooter toolCalls={insight.toolCalls} tone="dark" />
            ) : null}
          </div>
        </div>
      </article>
    );
  }

  const messageCount = insight.thread.length;
  return (
    <article className="rounded-xl border border-[var(--line)] bg-white p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        ↘ {dateLabel}
      </p>
      {insight.anomalyHint ? (
        <p className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>anomaly · {anomalyLabel}</Trans>
        </p>
      ) : null}
      <p className="mt-2 font-serif text-[15px] leading-tight text-[var(--ink)]">
        “{insight.headline}”
      </p>
      <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/75">{insight.body}</p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {'↘'} <Trans>what i'd do · {recommendation}</Trans>
      </p>
      {diffs && diffs.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {diffs.map((d) => (
            <DiffChip key={d.name} diff={d} tone="light" />
          ))}
        </div>
      ) : null}
      {insight.toolCalls && insight.toolCalls.length > 0 ? (
        <InvestigationFooter toolCalls={insight.toolCalls} tone="light" />
      ) : null}
      <div className="mt-3 flex justify-end">
        <button type="button" onClick={onOpenChat} className="link-quiet text-[11px]">
          {'↘'} {chatButtonLabel(messageCount, t)}
        </button>
      </div>
    </article>
  );
}

/** Button label for the chat trigger. Reads as a status when the
 *  thread already has turns ("3 messages") so the founder can see at
 *  a glance which reads they've pushed back on; reads as a call to
 *  action otherwise. Singular and plural handled. */
function chatButtonLabel(messageCount: number, t: T): string {
  if (messageCount === 0) return i18n._(msg`ask mag about this`);
  return plural(messageCount, { one: '# message', other: '# messages' });
}

function anomalyEyebrowLabel(hint: NonNullable<CampaignInsight['anomalyHint']>, t: T): string {
  const kindLabel =
    hint.kind === 'spike'
      ? i18n._(msg`spike`)
      : hint.kind === 'drop'
        ? i18n._(msg`drop`)
        : hint.kind === 'funnel_break'
          ? i18n._(msg`funnel break`)
          : i18n._(msg`shift`);
  // s198 — paid-ad anomalies anchor on the deliverable's hook, not
  // on an analytics event name.
  const hook = hint.paid_ad ? truncate(hint.paid_ad.hook, 40) : null;
  const anchor =
    hook !== null
      ? i18n._(msg`paid ad "${hook}"`)
      : (hint.event_name ?? i18n._(msg`this campaign`));
  return i18n._(msg`${kindLabel} on ${anchor}`);
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

interface EventDiff {
  name: string;
  baselineCount: number;
  currentCount: number;
  pctChange: number | null;
}

function computeEventDiffs(
  current: Record<string, number>,
  baseline: Record<string, number>,
): EventDiff[] {
  const allNames = new Set([...Object.keys(current), ...Object.keys(baseline)]);
  const diffs: EventDiff[] = [];
  for (const name of allNames) {
    const baselineCount = baseline[name] ?? 0;
    const currentCount = current[name] ?? 0;
    if (baselineCount === currentCount) continue;
    const pctChange =
      baselineCount === 0 ? null : ((currentCount - baselineCount) / baselineCount) * 100;
    diffs.push({ name, baselineCount, currentCount, pctChange });
  }
  diffs.sort((a, b) => Math.abs(b.pctChange ?? 0) - Math.abs(a.pctChange ?? 0));
  return diffs.slice(0, 5);
}

function DiffChip({ diff, tone }: { diff: EventDiff; tone: 'dark' | 'light' }) {
  const isUp = diff.currentCount > diff.baselineCount;
  const arrow = isUp ? '↑' : '↓';
  const pctLabel =
    diff.pctChange === null
      ? ''
      : `${diff.pctChange >= 0 ? '+' : ''}${Math.round(diff.pctChange)}%`;
  const base =
    tone === 'dark'
      ? 'border-[var(--spell)]/35 bg-[var(--ink)]/40 text-[var(--cream)]'
      : 'border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]/80';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10.5px] leading-none ${base}`}
      title={`${diff.name}: ${diff.baselineCount} → ${diff.currentCount}`}
    >
      <span className="font-medium">{diff.name}</span>
      <span className="opacity-60">·</span>
      <span>
        {diff.baselineCount} {arrow} {diff.currentCount}
      </span>
      {pctLabel ? <span className="opacity-70">{pctLabel}</span> : null}
    </span>
  );
}

function GoalBlock({
  campaignId,
  goal,
  fallbackGoal,
}: {
  campaignId: string;
  goal: string | null;
  fallbackGoal: string | null;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const initialDraft = goal ?? fallbackGoal ?? '';
  const [draft, setDraft] = useState(initialDraft);
  const [submitting, setSubmitting] = useState(false);
  const hasExplicit = Boolean(goal);
  const hasFallback = !hasExplicit && Boolean(fallbackGoal);
  const displayValue = goal ?? fallbackGoal ?? null;

  useEffect(() => {
    if (!editing) setDraft(goal ?? fallbackGoal ?? '');
  }, [goal, fallbackGoal, editing]);

  const onCancel = () => {
    setEditing(false);
    setDraft(goal ?? fallbackGoal ?? '');
  };

  const onSave = async () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      await persist(null);
      return;
    }
    if (trimmed.length > 200) {
      magToast.error(t`that goal is too long.`, t`keep it under 200 characters.`);
      return;
    }
    await persist(trimmed);
  };

  const persist = async (value: string | null) => {
    setSubmitting(true);
    try {
      await setCampaignGoal(campaignId, value);
      await refreshQueries(queryClient, ['campaign', campaignId]);
      await router.invalidate();
      setEditing(false);
    } catch (err) {
      const message = founderErrorCopy(err, 'try again.');
      magToast.error("couldn't save the goal.", message);
    } finally {
      setSubmitting(false);
    }
  };

  if (editing) {
    return (
      <div className="rounded-2xl border border-[var(--ink)] bg-white p-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} {hasExplicit ? t`edit the goal` : hasFallback ? t`sharpen the goal` : t`add a goal`}
        </p>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t`drive landing→paid above 5%`}
          rows={3}
          maxLength={220}
          disabled={submitting}
          // biome-ignore lint/a11y/noAutofocus: editor expands inline on click; focusing the textarea is the click's whole purpose.
          autoFocus
          className="mt-3 w-full rounded-lg border border-[var(--ink)] bg-white px-3 py-2 font-serif text-[16px] leading-tight outline-none focus:shadow-[3px_3px_0_var(--plum)] disabled:opacity-60"
        />
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>
            one sentence in your voice — what this campaign is actually trying to move. mag reads
            this on every scan.
          </Trans>
        </p>
        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="link-quiet text-[12px] disabled:opacity-60"
          >
            <Trans>cancel</Trans>
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={submitting}
            className="btn disabled:opacity-60"
          >
            {submitting ? t`saving…` : t`save goal`}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </div>
    );
  }

  const isEmpty = !displayValue;
  return (
    <div
      className={
        isEmpty
          ? 'rounded-2xl border-[1.5px] border-dashed border-[var(--ink)]/30 bg-[var(--paper)] px-6 py-5'
          : 'rounded-2xl border-[1.5px] border-[var(--ink)] bg-[var(--paper)] px-6 py-5 shadow-[4px_4px_0_var(--plum)]'
      }
    >
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--plum)]">
          {'↘'} {hasExplicit ? t`the goal` : hasFallback ? t`working goal` : t`no goal yet`}
        </p>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="link-quiet text-[11px]"
          aria-label={hasExplicit ? t`edit goal` : hasFallback ? t`sharpen goal` : t`add goal`}
        >
          {'↘'} {hasExplicit ? t`edit` : hasFallback ? t`+ sharpen this` : t`+ add a goal`}
        </button>
      </div>
      {displayValue ? (
        <>
          <p className="mt-3 font-serif text-[26px] leading-[1.15] tracking-[-0.005em] text-[var(--ink)] sm:text-[30px]">
            “{displayValue}”
          </p>
          {hasFallback ? (
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
              {'↘'} <Trans>pulled from your install promise — sharpen it any time</Trans>
            </p>
          ) : null}
        </>
      ) : (
        <p className="mt-3 text-[14px] leading-[1.55] text-[var(--ink)]/70">
          <Trans>
            one sentence in your voice — what this campaign is actually trying to move. e.g. “drive
            landing→paid above 5%” or “cut wizard step-3 drop-off below 30%”. mag reads this against
            every scan; until you set one, she picks a working anchor from the event set.
          </Trans>
        </p>
      )}
    </div>
  );
}

/** s163 + s164 — followups panel on the campaign page. Renders two
 *  sections: open followups (the active asks mag is chasing) and a
 *  collapsible history of recently-closed ones (done + abandoned).
 *  Both share the same row component visually; only the open ones
 *  carry the done/working/drop actions. */
function FollowupsPanel({
  open,
  closed,
  campaignId,
  campaignName,
}: {
  open: MagFollowup[];
  closed: MagFollowup[];
  campaignId: string;
  campaignName: string | null;
}) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const refresh = async () => {
    await refreshQueries(queryClient, ['campaign', campaignId]);
    await router.invalidate();
  };
  // s165 — detail drawer state. Lifted here so clicks from both
  // open + closed rows route through the same handler.
  const [detailFollowup, setDetailFollowup] = useState<MagFollowup | null>(null);
  return (
    <section className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-5">
      {open.length > 0 ? (
        <>
          <header>
            <p className="font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>open followups · {open.length}</Trans>
            </p>
            <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/65">
              <Trans>
                things i asked you to ship. i'll auto-close anything i can detect from your events;
                the rest is on you to mark.
              </Trans>
            </p>
          </header>
          <ol className="mt-4 flex flex-col gap-3">
            {open.map((f) => (
              <OpenFollowupRow
                key={f.id}
                followup={f}
                onResolved={refresh}
                onOpenDetail={() => setDetailFollowup(f)}
              />
            ))}
          </ol>
        </>
      ) : (
        <header>
          <p className="font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>followups · nothing open</Trans>
          </p>
          <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/65">
            <Trans>you're clear. nothing i'm chasing you on for this campaign.</Trans>
          </p>
        </header>
      )}

      {closed.length > 0 ? (
        <details className={open.length > 0 ? 'mt-5' : 'mt-4'}>
          <summary className="cursor-pointer select-none font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-[var(--ink)]/55 hover:text-[var(--ink)]">
            {'↘'} <Trans>history · {closed.length} closed</Trans>
          </summary>
          <ol className="mt-3 flex flex-col gap-2">
            {closed.map((f) => (
              <ClosedFollowupRow
                key={f.id}
                followup={f}
                onOpenDetail={() => setDetailFollowup(f)}
              />
            ))}
          </ol>
        </details>
      ) : null}

      {detailFollowup ? (
        <FollowupDetailDrawer
          followup={detailFollowup}
          campaignName={campaignName ?? undefined}
          onClose={() => setDetailFollowup(null)}
        />
      ) : null}
    </section>
  );
}

/** One row inside the OpenFollowupsPanel. Compact by default; the
 *  founder taps "↘ what i asked" to expand the original mag reply
 *  with the full coding-agent prompt block. The summary itself is
 *  a button that opens the full FollowupDetailDrawer (s165). */
function OpenFollowupRow({
  followup,
  onResolved,
  onOpenDetail,
}: {
  followup: MagFollowup;
  onResolved: () => Promise<void>;
  onOpenDetail: () => void;
}) {
  const { t } = useLingui();
  const summary = followup.summary;
  const [submitting, setSubmitting] = useState<null | 'done' | 'working' | 'drop'>(null);
  const [expanded, setExpanded] = useState(false);

  const click = async (action: 'done' | 'working' | 'drop') => {
    setSubmitting(action);
    try {
      await resolveFollowup(followup.id, action);
      if (action === 'done') {
        magToast.success(t`nice.`, t`mag's marked it done. next read will look at the impact.`);
      } else if (action === 'working') {
        magToast.info(t`got it.`, t`i'll check back tomorrow.`);
      } else {
        magToast.info(t`dropped.`, t`out of my queue.`);
      }
      await onResolved();
    } catch (err) {
      magToast.error("couldn't update the followup.", founderErrorCopy(err));
    } finally {
      setSubmitting(null);
    }
  };

  // Signal label: shows the founder how mag will detect this. Helps
  // build trust ("i'll close this automatically when wizard_started
  // fires with wizard_type" reads way better than just "manual").
  const signalLabel = renderSignalLabel(followup, t);
  const ageLabel = renderAgeLabel(followup.createdAt, t);

  return (
    <li className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4">
      <div className="flex items-baseline justify-between gap-3">
        <button
          type="button"
          onClick={onOpenDetail}
          className="link-lift text-left font-serif text-[15px] leading-tight"
          aria-label={t`open followup details — ${summary}`}
        >
          <Trans>“{summary}”</Trans>
        </button>
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
          {ageLabel}
        </span>
      </div>
      <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
        ↘ {signalLabel}
      </p>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="link-quiet mt-3 text-[11px]"
      >
        {'↘'} {expanded ? t`hide what i asked` : t`what i asked`}
      </button>
      {expanded ? (
        <div className="mt-2 rounded-lg border border-[var(--line)] bg-white p-3">
          <MagMessageBody body={followup.body} />
        </div>
      ) : null}

      <div className="mt-3 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => click('drop')}
          disabled={submitting !== null}
          className="link-quiet text-[11px] disabled:opacity-60"
        >
          {submitting === 'drop' ? t`dropping…` : t`drop`}
        </button>
        <button
          type="button"
          onClick={() => click('working')}
          disabled={submitting !== null}
          className="btn btn-ghost text-[11px] disabled:opacity-60"
        >
          {submitting === 'working' ? t`noting…` : t`still working`}
          <span className="btn-arrow">→</span>
        </button>
        <button
          type="button"
          onClick={() => click('done')}
          disabled={submitting !== null}
          className="btn btn-spell disabled:opacity-60"
        >
          {submitting === 'done' ? t`marking…` : t`yes, done`}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </li>
  );
}

/** Closed (done / abandoned) followup row — compact, read-only,
 *  shows how it closed + when. Summary is a button that opens the
 *  same detail drawer as open rows (s165). No resolve actions; it's
 *  already done. */
function ClosedFollowupRow({
  followup,
  onOpenDetail,
}: {
  followup: MagFollowup;
  onOpenDetail: () => void;
}) {
  const { t } = useLingui();
  const summary = followup.summary;
  const isDone = followup.status === 'done';
  const closedAt = followup.completedAt ? new Date(followup.completedAt) : null;
  const closedLabel = closedAt
    ? formatDateTime(closedAt, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : t`unknown`;
  const closedByLabel = (() => {
    if (followup.completedBy === 'auto') return t`auto-closed — signal landed`;
    if (followup.completedBy === 'founder')
      return isDone ? t`you marked it done` : t`you dropped it`;
    if (followup.completedBy === 'mag') return t`mag closed it`;
    return '';
  })();
  return (
    <li
      className={`rounded-lg border px-3 py-2 ${
        isDone
          ? 'border-[var(--spell-soft)] bg-[var(--spell-soft)]/30'
          : 'border-[var(--line)] bg-[var(--paper)]'
      }`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <button
          type="button"
          onClick={onOpenDetail}
          className="link-lift text-left font-serif text-[13.5px] leading-tight"
          aria-label={t`open followup details — ${summary}`}
        >
          <Trans>“{summary}”</Trans>
        </button>
        <span className="shrink-0 font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
          <Trans>
            {isDone ? t`done` : t`dropped`} · {closedLabel}
          </Trans>
        </span>
      </div>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {'↘'} {closedByLabel}
        {!isDone &&
        followup.abandonedReason &&
        followup.abandonedReason !== 'dropped_by_founder' ? (
          <span className="ml-1.5 text-[var(--ink)]/75">· {followup.abandonedReason}</span>
        ) : null}
      </p>
    </li>
  );
}

function renderSignalLabel(followup: MagFollowup, t: T): string {
  const s = followup.completionSignal;
  if (s.kind === 'manual') {
    return i18n._(msg`i'll close this when you tell me — manual signal`);
  }
  if (s.kind === 'paid_conversions') {
    return plural(s.min_conversions ?? 1, {
      one: "i'll close when a conversion lands on this campaign",
      other: "i'll close when # conversions land on this campaign",
    });
  }
  const name = s.name;
  if (s.property && s.value) {
    const property = s.property;
    const value = s.value;
    return i18n._(msg`i'll close when ${name} fires with ${property}="${value}"`);
  }
  if (s.property) {
    const property = s.property;
    return i18n._(msg`i'll close when ${name} fires with ${property} set`);
  }
  return i18n._(msg`i'll close when ${name} starts firing`);
}

/** "opened today" / "opened yesterday" are Intl's words, not ours —
 *  numeric 'auto' produces them for a day offset. Only the older form
 *  stays a message, because "open 5 days" is not a relative-time
 *  phrase, it is a duration. */
function renderAgeLabel(createdAt: string, t: T): string {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 1) {
    const word = relativeDayWord(-days);
    return i18n._(msg`opened ${word}`);
  }
  return plural(days, { other: 'open # days' });
}
